import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { VoiceAudioEngine } from '../services/voiceAudioEngine';
import { VoiceSocketClient } from '../services/voiceSocketClient';
import VoiceAgentTab from '../components/testbed/VoiceAgentTab';
import PatientAiSummary from '../components/PatientAiSummary';
import { useAuth } from '../context/AuthContext';

function VoiceAi({ practiceId }) {
  const [activeTab, setActiveTab] = useState('calls');
  const [calls, setCalls] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);
  const [callbacks, setCallbacks] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [callsLoading, setCallsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showNewCallModal, setShowNewCallModal] = useState(false);
  const [voiceSession, setVoiceSession] = useState(null); // { patientId, campaignType, patient }
  const [preCallBriefing, setPreCallBriefing] = useState(null); // { patientId, campaignType, patient }
  const [filters, setFilters] = useState({
    status: '',
    outcome: '',
    dateRange: '7d'
  });
  const [callPrediction, setCallPrediction] = useState(null);
  const [recommendedSlots, setRecommendedSlots] = useState(null);

  // Load dashboard stats
  const loadDashboard = useCallback(async () => {
    if (!practiceId) return;
    try {
      const response = await api.getVoiceDashboard(practiceId, {
        dateRange: filters.dateRange
      });
      setDashboard(response.data);
    } catch (err) {
      console.error('Error loading voice dashboard:', err);
    }
  }, [practiceId, filters.dateRange]);

  // Load calls
  const loadCalls = useCallback(async () => {
    if (!practiceId) return;
    try {
      setCallsLoading(true);
      const response = await api.getVoiceCalls(practiceId, {
        status: filters.status || undefined,
        outcome: filters.outcome || undefined,
        limit: 50
      });
      setCalls(response.data || []);
    } catch (err) {
      console.error('Error loading calls:', err);
      setError('Failed to load calls');
    } finally {
      setCallsLoading(false);
      setLoading(false);
    }
  }, [practiceId, filters.status, filters.outcome]);

  // Load callbacks
  const loadCallbacks = useCallback(async () => {
    if (!practiceId) return;
    try {
      const response = await api.getCallbackTasks(practiceId, {
        status: 'pending',
        source: 'voice_ai'
      });
      // Combine first/last name into patient_name for display
      const data = (response.data || []).map(cb => ({
        ...cb,
        patient_name: cb.patient_name || [cb.patient_first_name, cb.patient_last_name].filter(Boolean).join(' ') || 'Unknown'
      }));
      setCallbacks(data);
    } catch (err) {
      console.error('Error loading callbacks:', err);
    }
  }, [practiceId]);

  // Load call details
  const loadCallDetails = async (callId) => {
    try {
      const response = await api.getVoiceCall(practiceId, callId);
      setSelectedCall(response.data);
    } catch (err) {
      console.error('Error loading call details:', err);
      setError('Failed to load call details');
    }
  };

  // Initial load
  useEffect(() => {
    loadDashboard();
    loadCalls();
    loadCallbacks();
  }, [loadDashboard, loadCalls, loadCallbacks]);

  // Polling for updates
  useEffect(() => {
    const interval = setInterval(() => {
      loadCalls();
      loadCallbacks();
      loadDashboard();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadCalls, loadCallbacks, loadDashboard]);

  // Handle completing a callback
  const handleCompleteCallback = async (callbackId, notes) => {
    try {
      await api.completeCallbackTask(practiceId, callbackId, notes);
      await loadCallbacks();
    } catch (err) {
      console.error('Error completing callback:', err);
      alert(`Failed to complete callback: ${err.message}`);
    }
  };

  // Handle initiating a new call — show pre-call briefing first
  const handleInitiateCall = async (patientId, campaignType, patient) => {
    setShowNewCallModal(false);
    setPreCallBriefing({ patientId, campaignType, patient });
    setCallPrediction(null);
    setRecommendedSlots(null);

    // Fetch call prediction and recommended slots in parallel
    api.getCallPrediction(practiceId, patientId).then(r => {
      if (r.success && r.data) setCallPrediction(r.data);
    }).catch(() => {});
    api.getRecommendedSlots(practiceId, patientId).then(r => {
      if (r.success && r.data) setRecommendedSlots(r.data);
    }).catch(() => {});
  };

  // Start call after reviewing briefing
  const handleStartCallFromBriefing = () => {
    setVoiceSession(preCallBriefing);
    setPreCallBriefing(null);
  };

  // Cancel pre-call briefing
  const handleCancelBriefing = () => {
    setPreCallBriefing(null);
  };

  // Handle voice session ended
  const handleVoiceSessionEnd = () => {
    setVoiceSession(null);
    loadCalls();
    loadCallbacks();
    loadDashboard();
  };

  if (!practiceId) {
    return (
      <div style={styles.noPractice}>
        <div style={styles.noPracticeCard}>
          <AlertIcon />
          <h3 style={styles.noPracticeTitle}>No Practice Selected</h3>
          <p style={styles.noPracticeText}>
            Please select a practice to view voice AI calls.
          </p>
        </div>
      </div>
    );
  }

  // Pre-call briefing panel
  if (preCallBriefing) {
    return (
      <div style={briefingStyles.container}>
        <div style={briefingStyles.card}>
          <div style={briefingStyles.header}>
            <div>
              <h2 style={briefingStyles.title}>Pre-Call Briefing</h2>
              <p style={briefingStyles.subtitle}>
                {preCallBriefing.patient
                  ? `${preCallBriefing.patient.first_name} ${preCallBriefing.patient.last_name}`
                  : 'Patient'}
                {preCallBriefing.campaignType && ` — ${preCallBriefing.campaignType}`}
              </p>
            </div>
            <button onClick={handleCancelBriefing} style={briefingStyles.cancelBtn}>Cancel</button>
          </div>
          <div style={briefingStyles.summaryArea}>
            <PatientAiSummary
              practiceId={practiceId}
              patientId={preCallBriefing.patientId}
              context="pre_call"
            />
          </div>

          {/* AI Call Prediction + Recommended Slots */}
          {(callPrediction || recommendedSlots) && (
            <div style={briefingStyles.aiStrip}>
              {callPrediction && (
                <div style={{
                  ...briefingStyles.aiCard,
                  borderColor: callPrediction.booking_probability >= 0.6 ? '#a7f3d0'
                    : callPrediction.booking_probability >= 0.3 ? '#fde68a' : '#fecaca',
                  background: callPrediction.booking_probability >= 0.6 ? '#ecfdf5'
                    : callPrediction.booking_probability >= 0.3 ? '#fffbeb' : '#fef2f2',
                }}>
                  <div style={briefingStyles.aiCardLabel}>Booking Probability</div>
                  <div style={{
                    fontSize: 24, fontWeight: 700,
                    color: callPrediction.booking_probability >= 0.6 ? '#059669'
                      : callPrediction.booking_probability >= 0.3 ? '#d97706' : '#dc2626',
                  }}>
                    {Math.round((callPrediction.booking_probability || 0) * 100)}%
                  </div>
                  {callPrediction.predicted_outcome && (
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                      Likely: {callPrediction.predicted_outcome}
                    </div>
                  )}
                </div>
              )}
              {recommendedSlots && Array.isArray(recommendedSlots) && recommendedSlots.length > 0 && (
                <div style={briefingStyles.aiCard}>
                  <div style={briefingStyles.aiCardLabel}>Recommended Slots</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
                    {recommendedSlots.slice(0, 3).map((slot, i) => (
                      <div key={i} style={{
                        fontSize: 12, color: '#374151',
                        padding: '3px 8px', background: '#f3f4f6', borderRadius: 4,
                      }}>
                        {slot.day_label || slot.day} {slot.hour_label || (slot.hour != null ? `${slot.hour % 12 || 12} ${slot.hour >= 12 ? 'PM' : 'AM'}` : '')}
                        {slot.provider_name && <span style={{ color: '#6b7280' }}> — {slot.provider_name}</span>}
                        {slot.score != null && <span style={{ color: '#9ca3af', marginLeft: 4 }}>({Math.round(slot.score * 100)}%)</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={briefingStyles.footer}>
            <button onClick={handleCancelBriefing} style={briefingStyles.backBtn}>Back</button>
            <button onClick={handleStartCallFromBriefing} style={briefingStyles.startCallBtn}>
              Start Call
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If voice session is active, show the voice panel
  if (voiceSession) {
    return (
      <VoiceSessionPanel
        practiceId={practiceId}
        patientId={voiceSession.patientId}
        campaignType={voiceSession.campaignType}
        patient={voiceSession.patient}
        onEnd={handleVoiceSessionEnd}
      />
    );
  }

  return (
    <div style={styles.pageContainer}>
      {/* Dashboard Stats Bar */}
      {dashboard && (
        <div style={styles.statsBar}>
          <button
            style={styles.newCallButton}
            onClick={() => setShowNewCallModal(true)}
          >
            <PhonePlusIcon />
            New Call
          </button>
          {/* The controller returns { stats, recent_calls, pending_callbacks } and the
              stats object carries total_calls plus by_status / by_outcome maps
              (VoiceController.getDashboard, CallLog.getStats). These four cards read
              dashboard.total_calls / .booked / .interested / .no_answer, none of which
              exist at the top level, so every card rendered 0 regardless of call volume.
              Found 2026-09-11 while auditing the e2e suite. */}
          <StatCard
            icon={<PhoneIcon />}
            label="Total Calls"
            value={dashboard.stats?.total_calls || 0}
            color="#3b82f6"
          />
          <StatCard
            icon={<CheckCircleIcon />}
            label="Booked"
            value={dashboard.stats?.by_outcome?.booked || 0}
            color="#10b981"
          />
          <StatCard
            icon={<UserIcon />}
            label="Interested"
            value={dashboard.stats?.by_outcome?.interested || 0}
            color="#8b5cf6"
          />
          <StatCard
            icon={<PhoneOffIcon />}
            label="No Answer"
            value={dashboard.stats?.by_outcome?.no_answer || 0}
            color="#f59e0b"
          />
          <StatCard
            icon={<CallbackIcon />}
            label="Callbacks"
            value={callbacks.length}
            color="#ef4444"
            highlight={callbacks.length > 0}
          />
        </div>
      )}

      {/* Tab Navigation */}
      <div style={styles.tabBar}>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'calls' ? styles.tabActive : {})
          }}
          onClick={() => setActiveTab('calls')}
        >
          <PhoneIcon />
          Calls
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'callbacks' ? styles.tabActive : {})
          }}
          onClick={() => setActiveTab('callbacks')}
        >
          <CallbackIcon />
          Callback Queue
          {callbacks.length > 0 && (
            <span style={styles.badge}>{callbacks.length}</span>
          )}
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'settings' ? styles.tabActive : {})
          }}
          onClick={() => setActiveTab('settings')}
        >
          <SettingsIcon />
          Settings
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'test' ? styles.tabActive : {})
          }}
          onClick={() => setActiveTab('test')}
        >
          <SparklesIcon />
          Test Agent
        </button>
      </div>

      {/* Main Content */}
      <div style={styles.mainContainer}>
        {activeTab === 'calls' && (
          <CallsView
            calls={calls}
            selectedCall={selectedCall}
            onSelectCall={(call) => {
              setSelectedCall(call);
              if (call) loadCallDetails(call.id);
            }}
            filters={filters}
            onFiltersChange={setFilters}
            loading={loading || callsLoading}
            error={error}
            onClearError={() => setError(null)}
          />
        )}

        {activeTab === 'callbacks' && (
          <CallbacksView
            callbacks={callbacks}
            onComplete={handleCompleteCallback}
            onRefresh={loadCallbacks}
            practiceId={practiceId}
          />
        )}

        {activeTab === 'settings' && (
          <SettingsView practiceId={practiceId} />
        )}

        {activeTab === 'test' && (
          <VoiceAgentTab practiceId={practiceId} />
        )}
      </div>

      {/* New Call Modal */}
      {showNewCallModal && (
        <NewCallModal
          practiceId={practiceId}
          onClose={() => setShowNewCallModal(false)}
          onInitiateCall={handleInitiateCall}
        />
      )}
    </div>
  );
}

// Calls View Component
function CallsView({ calls, selectedCall, onSelectCall, filters, onFiltersChange, loading, error, onClearError }) {
  return (
    <div style={styles.splitView}>
      {/* Left Sidebar - Call List */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <h2 style={styles.sidebarTitle}>Recent Calls</h2>
          <div style={styles.filterRow}>
            <select
              style={styles.filterSelect}
              value={filters.status}
              onChange={(e) => onFiltersChange({ ...filters, status: e.target.value })}
            >
              <option value="">All Status</option>
              <option value="answered">Answered</option>
              <option value="voicemail">Voicemail</option>
              <option value="no_answer">No Answer</option>
              <option value="failed">Failed</option>
            </select>
            <select
              style={styles.filterSelect}
              value={filters.outcome}
              onChange={(e) => onFiltersChange({ ...filters, outcome: e.target.value })}
            >
              <option value="">All Outcomes</option>
              <option value="booked">Booked</option>
              <option value="interested">Interested</option>
              <option value="declined">Declined</option>
              <option value="callback_requested">Callback</option>
              <option value="opt_out">Opt-Out</option>
            </select>
          </div>
        </div>

        {/* Call List */}
        <div style={styles.listContainer}>
          {loading ? (
            <div style={styles.loadingState}>
              <LoadingSpinner />
              <span>Loading calls...</span>
            </div>
          ) : calls.length === 0 ? (
            <div style={styles.emptyList}>
              <PhoneIcon />
              <p>No calls found</p>
            </div>
          ) : (
            calls.map((call) => (
              <div
                key={call.id}
                style={{
                  ...styles.callItem,
                  ...(selectedCall?.id === call.id ? styles.callItemSelected : {})
                }}
                onClick={() => onSelectCall(call)}
              >
                <div style={styles.callItemHeader}>
                  <span style={styles.patientName}>
                    {call.patient_name
                      || (call.probable_patient_name ? <>Likely {call.probable_patient_name} <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 400 }}>· by phone, not verified</span></> : 'Unknown caller')}
                  </span>
                  <StatusBadge status={call.status} outcome={call.outcome} />
                </div>
                <div style={styles.callItemDetails}>
                  <span style={styles.phoneNumber}>{call.phone_number}</span>
                  <span style={styles.callTime}>
                    {formatRelativeTime(call.created_at)}
                  </span>
                </div>
                {/* GH-180: show absolute start → end wall-clock times so staff
                    doesn't have to hover to see when the call happened. Falls
                    back to just start time when end_time isn't populated. */}
                <div style={styles.callTimeRange}>
                  <ClockIcon />
                  <span>
                    {formatCallTimeRange(call.started_at || call.created_at, call.ended_at, call.duration_seconds)}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right Side - Call Details */}
      <div style={styles.mainContent}>
        {error && (
          <div style={styles.errorBanner}>
            <AlertIcon />
            <span>{error}</span>
            <button onClick={onClearError} style={styles.errorClose}>
              <CloseIcon />
            </button>
          </div>
        )}

        {selectedCall ? (
          <CallDetail call={selectedCall} />
        ) : (
          <div style={styles.emptyState}>
            <PhoneIcon style={{ width: 64, height: 64 }} />
            <h3 style={styles.emptyTitle}>Select a call</h3>
            <p style={styles.emptyText}>
              Choose a call from the list to view details and transcript
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Call Detail Component
function CallDetail({ call }) {
  const [showFullTranscript, setShowFullTranscript] = useState(false);

  return (
    <div style={styles.callDetail}>
      {/* Header */}
      <div style={styles.detailHeader}>
        <div style={styles.detailHeaderInfo}>
          <div style={styles.patientAvatar}>
            {getInitials(call.patient_name || call.probable_patient_name)}
          </div>
          <div>
            <h2 style={styles.detailPatientName}>{call.patient_name || (call.probable_patient_name ? `Likely ${call.probable_patient_name}` : 'Unknown caller')}</h2>
            {!call.patient_name && call.probable_patient_name && (
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280' }}>Matched by phone number only — the caller did not verify, so this call is not filed under a patient.</p>
            )}
            <p style={styles.detailPhone}>{call.phone_number}</p>
          </div>
        </div>
        <StatusBadge status={call.status} outcome={call.outcome} large />
      </div>

      {/* Call Info Grid */}
      <div style={styles.infoGrid}>
        <InfoCard label="Date & Time" value={formatDateTime(call.created_at)} icon={<CalendarIcon />} />
        <InfoCard label="Duration" value={call.duration_seconds ? formatDuration(call.duration_seconds) : 'N/A'} icon={<ClockIcon />} />
        <InfoCard label="Outcome" value={formatOutcome(call.outcome)} icon={<CheckCircleIcon />} />
        <InfoCard label="End Reason" value={formatEndReason(call.end_reason)} icon={<InfoIcon />} />
      </div>

      {/* AI Summary */}
      {call.ai_summary && (
        <div style={styles.summarySection}>
          <h3 style={styles.sectionTitle}>
            <SparklesIcon />
            AI Summary
          </h3>
          <p style={styles.summaryText}>{call.ai_summary}</p>
        </div>
      )}

      {/* Structured Data + Success Evaluation */}
      <CallAnalysisPanel call={call} />

      {/* Transcript */}
      {call.transcript && (
        <div style={styles.transcriptSection}>
          <div style={styles.transcriptHeader}>
            <h3 style={styles.sectionTitle}>
              <TranscriptIcon />
              Transcript
            </h3>
            <button
              style={styles.toggleButton}
              onClick={() => setShowFullTranscript(!showFullTranscript)}
            >
              {showFullTranscript ? 'Show Less' : 'Show Full'}
            </button>
          </div>
          <div style={{
            ...styles.transcriptContent,
            maxHeight: showFullTranscript ? 'none' : '200px'
          }}>
            <pre style={styles.transcriptText}>{cleanTranscriptForDisplay(call.transcript)}</pre>
          </div>
        </div>
      )}

      {/* Recording (if available) */}
      {call.recording_url && (
        <div style={styles.recordingSection}>
          <h3 style={styles.sectionTitle}>
            <PlayIcon />
            Recording
          </h3>
          <audio controls style={styles.audioPlayer}>
            <source src={call.recording_url} type="audio/mpeg" />
            Your browser does not support audio playback.
          </audio>
        </div>
      )}
    </div>
  );
}

// Callbacks View Component
function CallbacksView({ callbacks, onComplete, onRefresh, practiceId }) {
  const [selectedCallback, setSelectedCallback] = useState(null);
  const [completionNotes, setCompletionNotes] = useState('');

  const handleComplete = async () => {
    if (!selectedCallback) return;
    await onComplete(selectedCallback.id, completionNotes);
    setSelectedCallback(null);
    setCompletionNotes('');
  };

  return (
    <div style={styles.callbacksContainer}>
      <div style={styles.callbacksHeader}>
        <div>
          <h2 style={styles.callbacksTitle}>Callback Queue</h2>
          <p style={styles.callbacksSubtitle}>
            Patients who requested a callback during their AI call
          </p>
        </div>
        <button style={styles.refreshButton} onClick={onRefresh}>
          <RefreshIcon />
          Refresh
        </button>
      </div>

      {callbacks.length === 0 ? (
        <div style={styles.emptyCallbacks}>
          <CheckCircleIcon style={{ width: 48, height: 48 }} />
          <h3 style={styles.emptyTitle}>All caught up!</h3>
          <p style={styles.emptyText}>No pending callbacks at this time.</p>
        </div>
      ) : (
        <div style={styles.callbacksGrid}>
          {callbacks.map((callback) => (
            <div
              key={callback.id}
              style={{
                ...styles.callbackCard,
                ...(callback.urgency === 'high' ? styles.callbackCardUrgent : {})
              }}
            >
              <div style={styles.callbackCardHeader}>
                <div style={styles.callbackPatient}>
                  <div style={styles.patientAvatar}>
                    {getInitials(callback.patient_name)}
                  </div>
                  <div>
                    <strong>{callback.patient_name}</strong>
                    <span style={styles.callbackPhone}>{callback.patient_phone}</span>
                  </div>
                </div>
                <UrgencyBadge urgency={callback.urgency} />
              </div>

              {callback.reason && (
                <div style={styles.callbackReason}>
                  <strong>Reason:</strong> {callback.reason}
                </div>
              )}

              <div style={styles.callbackMeta}>
                <span>Requested {formatRelativeTime(callback.created_at)}</span>
              </div>

              <div style={styles.callbackActions}>
                <a
                  href={`tel:${callback.patient_phone}`}
                  style={styles.callNowButton}
                >
                  <PhoneIcon />
                  Call Now
                </a>
                <button
                  style={styles.completeButton}
                  onClick={() => setSelectedCallback(callback)}
                >
                  <CheckIcon />
                  Mark Complete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Complete Callback Modal */}
      {selectedCallback && (
        <div style={styles.modalOverlay} onClick={() => setSelectedCallback(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Complete Callback</h3>
              <button
                style={styles.modalClose}
                onClick={() => setSelectedCallback(null)}
              >
                <CloseIcon />
              </button>
            </div>

            <div style={styles.modalBody}>
              <p style={styles.modalText}>
                Completing callback for <strong>{selectedCallback.patient_name}</strong>
              </p>

              <div style={styles.formGroup}>
                <label style={styles.label}>Notes (optional)</label>
                <textarea
                  style={styles.textarea}
                  placeholder="Add notes about the callback..."
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  rows={4}
                />
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                style={styles.cancelButton}
                onClick={() => setSelectedCallback(null)}
              >
                Cancel
              </button>
              <button style={styles.completeButtonPrimary} onClick={handleComplete}>
                <CheckIcon />
                Mark as Complete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Settings View Component
function SettingsView({ practiceId }) {
  // Phone assignments are super_admin-only (Phone Management). The forms
  // used to send the phone keys on every save, so practice_admins got a 403
  // and could not edit the greeting/prompt at all (audit C9, 2026-09-07).
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [assistants, setAssistants] = useState([]);
  const [voiceConfig, setVoiceConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingAssistant, setEditingAssistant] = useState(null);
  // Default to inbound (single-assistant config — most common starting point);
  // outbound shows the 5 campaign-type cards. Initial tab can be set via
  // ?sub=<key> query string so the legacy /voice-ai/flows redirect lands here.
  const initialSubTab = (() => {
    if (typeof window === 'undefined') return 'inbound';
    const params = new URLSearchParams(window.location.search);
    const sub = params.get('sub');
    return ['inbound', 'outbound', 'tools', 'profile'].includes(sub) ? sub : 'inbound';
  })();
  const [settingsTab, setSettingsTab] = useState(initialSubTab);
  const [editingConfig, setEditingConfig] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    system_prompt: '',
    first_message: '',
    voice_flow_id: null // carried through unchanged; the flow path is no longer routed
  });
  // Available voice flows for this practice — used by the Voice AI Mode selector
  const [configFormData, setConfigFormData] = useState({
    voice_phone_number: '',
    voice_max_daily_calls: 100,
    voice_provider: 'twilio_nova_sonic',
    voice_inbound_phone: '',
    endpointing_sensitivity: 'MEDIUM',
    voice_id: 'tiffany',
    temperature: 0.7,
    max_tokens: 1024
  });
  const [editingInbound, setEditingInbound] = useState(false);
  const [savingInbound, setSavingInbound] = useState(false);
  const [inboundFormData, setInboundFormData] = useState({
    voice_inbound_phone: '',
    name: '',
    system_prompt: '',
    first_message: '',
    voice_flow_id: null,
    enabled: true
  });
  // External tools state
  const [voiceTools, setVoiceTools] = useState([]);
  const [editingTool, setEditingTool] = useState(null); // null | 'new' | toolId
  const [toolFormData, setToolFormData] = useState({
    name: '', description: '', parameters: '[]',
    http_method: 'POST', url_template: '', headers: '{}',
    body_template: '', response_mapping: ''
  });
  const [savingTool, setSavingTool] = useState(false);
  const [seedingTools, setSeedingTools] = useState(false);
  const [savingWebhookConfig, setSavingWebhookConfig] = useState(false);
  const toolFormRef = useRef(null);
  const inboundFormRef = useRef(null);

  // Campaign types that can have voice assistants — loaded from the practice's
  // actual CampaignType rows so custom slugs (not just the five seeded
  // defaults) get a Configure card.
  const [campaignTypes, setCampaignTypes] = useState([]);

  // Load assistants and voice config
  useEffect(() => {
    const loadSettings = async () => {
      if (!practiceId) return;
      try {
        setLoading(true);
        const [assistantsRes, configRes, toolsRes, typesRes] = await Promise.all([
          api.getVoiceAssistants(practiceId, true),
          api.getVoiceConfig(practiceId),
          api.request(`/practices/${practiceId}/voice/tools`).catch(() => ({ data: [] })),
          api.getCampaignTypes(practiceId, true).catch(() => ({ data: [] }))
        ]);
        setAssistants(assistantsRes.data || []);
        setVoiceConfig(configRes.data || {});
        setVoiceTools(toolsRes.data || []);
        // Drive the assistant list off the practice's real campaign types
        // (system + custom). Fall back to the previous hardcoded set only if
        // the API returns nothing — keeps fresh practices usable before
        // initializeCampaignTypes has run.
        const typeRows = typesRes.data || [];
        const mapped = typeRows
          .filter(t => t.slug)
          .map(t => ({ slug: t.slug, label: t.name || t.slug, is_system: !!t.is_system }));
        setCampaignTypes(mapped.length > 0 ? mapped : [
          { slug: 'reactivation', label: 'Reactivation Campaign' },
          { slug: 'no-show', label: 'No-Show Follow-up' },
          { slug: 'cancellation', label: 'Cancellation Recovery' },
          { slug: 'treatment-followup', label: 'Treatment Follow-up' },
          { slug: 'recall', label: 'Recall/Hygiene' }
        ]);
        setConfigFormData({
          voice_phone_number: configRes.data?.voice_phone_number || '',
          voice_max_daily_calls: configRes.data?.voice_max_daily_calls || 100,
          voice_provider: configRes.data?.voice_provider || 'twilio_nova_sonic',
          voice_inbound_phone: configRes.data?.voice_inbound_phone || '',
          endpointing_sensitivity: configRes.data?.endpointing_sensitivity || 'MEDIUM',
          voice_id: configRes.data?.voice_id || 'tiffany',
          temperature: configRes.data?.temperature ?? 0.7,
          max_tokens: configRes.data?.max_tokens || 1024
        });
      } catch (err) {
        console.error('Error loading settings:', err);
        setError('Failed to load voice settings');
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, [practiceId]);

  // Get assistant for a campaign type
  const getAssistantForType = (campaignType) => {
    return assistants.find(a => a.campaign_type === campaignType);
  };

  // Handle voice config save
  const handleSaveConfig = async () => {
    // Validate based on selected provider
    // nova_sonic_websocket has no additional required fields

    try {
      setSavingConfig(true);
      setError(null);

      const configPayload = {
        voice_max_daily_calls: parseInt(configFormData.voice_max_daily_calls, 10) || 100,
        voice_provider: configFormData.voice_provider
      };
      if (isSuperAdmin) {
        configPayload.voice_phone_number = configFormData.voice_phone_number?.trim() || '';
      }
      await api.updateVoiceConfig(practiceId, configPayload);

      const configRes = await api.getVoiceConfig(practiceId);
      setVoiceConfig(configRes.data || {});
      setSuccess('Voice configuration saved successfully');
      setEditingConfig(false);
    } catch (err) {
      console.error('Error saving voice config:', err);
      setError(`Failed to save configuration: ${err.message}`);
    } finally {
      setSavingConfig(false);
    }
  };

  // Handle edit click
  const handleEdit = (campaignType) => {
    const existing = getAssistantForType(campaignType);
    setEditingAssistant(campaignType);
    setFormData({
      name: existing?.name || '',
      system_prompt: existing?.system_prompt || '',
      first_message: existing?.first_message || '',
      voice_flow_id: existing?.voice_flow_id ?? null
    });
    setError(null);
    setSuccess(null);
  };

  // Handle save
  const handleSave = async () => {
    if (!editingAssistant) return;
    // System prompt is only required when not using a Flow (Flow mode compiles its own prompt)

    try {
      setSaving(true);
      setError(null);

      await api.updateVoiceAssistant(practiceId, editingAssistant, {
        name: formData.name.trim() || `${editingAssistant} Assistant`,
        system_prompt: formData.system_prompt.trim(),
        first_message: formData.first_message.trim() || null,
        voice_flow_id: formData.voice_flow_id // null = direct prompt path; int = compile this flow
      });

      // Reload assistants
      const response = await api.getVoiceAssistants(practiceId, true);
      setAssistants(response.data || []);

      setSuccess('Assistant configuration saved successfully');
      setEditingAssistant(null);
    } catch (err) {
      console.error('Error saving assistant:', err);
      setError(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Tool CRUD handlers
  const handleEditTool = (tool) => {
    setEditingTool(tool.id);
    setToolFormData({
      name: tool.name || '',
      description: tool.description || '',
      parameters: tool.parameters || '[]',
      http_method: tool.http_method || 'POST',
      url_template: tool.url_template || '',
      headers: tool.headers || '{}',
      body_template: tool.body_template || '',
      response_mapping: tool.response_mapping || ''
    });
    setTimeout(() => toolFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleNewTool = () => {
    setEditingTool('new');
    setToolFormData({
      name: '', description: '', parameters: '[]',
      http_method: 'POST', url_template: '', headers: '{}',
      body_template: '', response_mapping: ''
    });
    setTimeout(() => toolFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleSaveTool = async () => {
    if (!toolFormData.name.trim() || !toolFormData.url_template.trim()) {
      setError('Tool name and URL are required');
      return;
    }
    try {
      setSavingTool(true);
      setError(null);
      if (editingTool === 'new') {
        await api.request(`/practices/${practiceId}/voice/tools`, {
          method: 'POST',
          body: JSON.stringify(toolFormData)
        });
      } else {
        await api.request(`/practices/${practiceId}/voice/tools/${editingTool}`, {
          method: 'PUT',
          body: JSON.stringify(toolFormData)
        });
      }
      const toolsRes = await api.request(`/practices/${practiceId}/voice/tools`);
      setVoiceTools(toolsRes.data || []);
      setSuccess('Tool saved successfully');
      setEditingTool(null);
    } catch (err) {
      setError(`Failed to save tool: ${err.message}`);
    } finally {
      setSavingTool(false);
    }
  };

  const handleDeleteTool = async (toolId) => {
    if (!window.confirm('Delete this tool?')) return;
    try {
      await api.request(`/practices/${practiceId}/voice/tools/${toolId}`, { method: 'DELETE' });
      setVoiceTools(prev => prev.filter(t => t.id !== toolId));
      setSuccess('Tool deleted');
    } catch (err) {
      setError(`Failed to delete tool: ${err.message}`);
    }
  };


  const handleSeedDefaults = async () => {
    try {
      setSeedingTools(true);
      setError(null);
      await api.request(`/practices/${practiceId}/voice/tools/seed-defaults`, {
        method: 'POST',
        body: JSON.stringify({})
      });
      const toolsRes = await api.request(`/practices/${practiceId}/voice/tools`);
      setVoiceTools(toolsRes.data || []);
      setSuccess(`${toolsRes.data?.length || 15} default PMS tools loaded successfully`);
    } catch (err) {
      const msg = err.message || 'Failed to seed tools';
      setError(msg.includes('already has') ? msg : `Failed to load defaults: ${msg}`);
    } finally {
      setSeedingTools(false);
    }
  };

  const handleSaveWebhookConfig = async () => {
    try {
      setSavingWebhookConfig(true);
      setError(null);
      await api.updateVoiceConfig(practiceId, {
        endpointing_sensitivity: configFormData.endpointing_sensitivity,
        voice_id: configFormData.voice_id,
        temperature: parseFloat(configFormData.temperature),
        max_tokens: parseInt(configFormData.max_tokens, 10)
      });
      const configRes = await api.getVoiceConfig(practiceId);
      setVoiceConfig(configRes.data || {});
      setSuccess('Configuration saved');
    } catch (err) {
      setError(`Failed to save webhook config: ${err.message}`);
    } finally {
      setSavingWebhookConfig(false);
    }
  };

  // Inbound handlers
  const handleEditInbound = () => {
    const inboundAssistant = getAssistantForType('inbound');
    setEditingInbound(true);
    setInboundFormData({
      voice_inbound_phone: voiceConfig?.voice_inbound_phone || '',
      name: inboundAssistant?.name || 'Inbound Assistant',
      system_prompt: inboundAssistant?.system_prompt || '',
      first_message: inboundAssistant?.first_message || '',
      voice_flow_id: inboundAssistant?.voice_flow_id ?? null,
      enabled: !(inboundAssistant?.enabled === 0 || inboundAssistant?.enabled === false)
    });
    setTimeout(() => inboundFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleSaveInbound = async () => {
    // Prompt is only required when not using a Flow
    try {
      setSavingInbound(true);
      setError(null);

      // Save inbound phone number — super_admin only (Phone Management owns it)
      if (isSuperAdmin && (inboundFormData.voice_inbound_phone || '').trim() !== (voiceConfig?.voice_inbound_phone || '')) {
        await api.updateVoiceConfig(practiceId, {
          voice_inbound_phone: inboundFormData.voice_inbound_phone.trim()
        });
      }

      // Save inbound assistant
      await api.updateVoiceAssistant(practiceId, 'inbound', {
        name: inboundFormData.name.trim() || 'Inbound Assistant',
        system_prompt: inboundFormData.system_prompt.trim(),
        first_message: inboundFormData.first_message.trim() || null,
        voice_flow_id: inboundFormData.voice_flow_id,
        enabled: inboundFormData.enabled !== false
      });

      // Reload
      const [configRes, assistantsRes] = await Promise.all([
        api.getVoiceConfig(practiceId),
        api.getVoiceAssistants(practiceId, true)
      ]);
      setVoiceConfig(configRes.data || {});
      setAssistants(assistantsRes.data || []);
      setConfigFormData(prev => ({ ...prev, voice_inbound_phone: configRes.data?.voice_inbound_phone || '' }));
      setSuccess('Inbound configuration saved');
      setEditingInbound(false);
    } catch (err) {
      setError(`Failed to save inbound config: ${err.message}`);
    } finally {
      setSavingInbound(false);
    }
  };

  const currentProvider = voiceConfig?.voice_provider || configFormData.voice_provider;
  const isNovaSonic = currentProvider === 'nova_sonic_websocket' || currentProvider === 'twilio_nova_sonic';

  return (
    <div style={styles.settingsContainer}>
      <div style={styles.settingsHeader}>
        <div>
          <h2 style={styles.settingsTitle}>Voice AI Settings</h2>
          <p style={styles.settingsSubtitle}>
            Configure voice AI for outbound campaigns, inbound patient calls, and tool integrations
          </p>
        </div>
      </div>

      {error && (
        <div style={styles.alertError}>
          <AlertIcon />
          <span>{error}</span>
          <button onClick={() => setError(null)} style={styles.alertClose}>
            <CloseIcon />
          </button>
        </div>
      )}

      {success && (
        <div style={styles.alertSuccess}>
          <CheckCircleIcon />
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} style={styles.alertClose}>
            <CloseIcon />
          </button>
        </div>
      )}

      {/* Tab Bar */}
      <div style={settingsTabStyles.tabBar}>
        {[
          { key: 'inbound', label: 'Inbound' },
          { key: 'outbound', label: 'Outbound' },
          { key: 'tools', label: 'Tools' },
          { key: 'profile', label: 'Practice Profile' }
        ].map(tab => (
          <button
            key={tab.key}
            style={{
              ...settingsTabStyles.tab,
              ...(settingsTab === tab.key ? settingsTabStyles.tabActive : {})
            }}
            onClick={() => { setSettingsTab(tab.key); setError(null); setSuccess(null); }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={styles.loadingState}>
          <LoadingSpinner />
          <span>Loading settings...</span>
        </div>
      ) : (
        <div style={styles.settingsContent}>

          {/* ========== OUTBOUND TAB ========== */}
          {settingsTab === 'outbound' && (
            <>
              {/* Provider Configuration */}
              <div style={styles.settingsSection}>
                <h3 style={styles.sectionHeader}>
                  <PhoneIcon />
                  Voice Provider
                </h3>
                <p style={styles.sectionDescription}>
                  Choose how outbound voice calls are handled.
                </p>

                <div style={styles.assistantCard}>
                  {editingConfig ? (
                    <div style={styles.editForm}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>
                          Voice Provider <span style={styles.required}>*</span>
                        </label>
                        <select
                          style={styles.input}
                          value={configFormData.voice_provider}
                          onChange={(e) => setConfigFormData({ ...configFormData, voice_provider: e.target.value })}
                        >
                          <option value="twilio_nova_sonic">Twilio + Nova Sonic (Phone calls via Twilio)</option>
                          <option value="nova_sonic_websocket">Nova Sonic (Browser-based, AWS Bedrock)</option>
                        </select>
                      </div>

                      {configFormData.voice_provider === 'twilio_nova_sonic' && (
                        <div style={settingsTabStyles.infoBox}>
                          Twilio + Nova Sonic uses Twilio for phone calls and AWS Bedrock Nova Sonic for AI.
                          Requires <code>TWILIO_ACCOUNT_SID</code> and <code>TWILIO_AUTH_TOKEN</code> in .env.
                          The Inbound Phone Number (configured below) is used as the caller ID for outbound calls.
                        </div>
                      )}

                      {configFormData.voice_provider === 'nova_sonic_websocket' && (
                        <div style={settingsTabStyles.infoBox}>
                          Nova Sonic uses the browser microphone and speaker â€" no phone infrastructure needed.
                          Ensure AWS credentials have <code>bedrock:InvokeModelWithBidirectionalStream</code> permission.
                        </div>
                      )}

                      <div style={styles.formGroup}>
                        <label style={styles.label}>Max Daily Calls</label>
                        <input
                          type="number"
                          style={styles.input}
                          placeholder="100"
                          value={configFormData.voice_max_daily_calls}
                          onChange={(e) => setConfigFormData({ ...configFormData, voice_max_daily_calls: e.target.value })}
                          min="1"
                          max="1000"
                        />
                      </div>

                      <div style={styles.editActions}>
                        <button
                          style={styles.cancelButton}
                          onClick={() => {
                            setEditingConfig(false);
                            setConfigFormData({
                              voice_phone_number: voiceConfig?.voice_phone_number || '',
                              voice_max_daily_calls: voiceConfig?.voice_max_daily_calls || 100,
                              voice_provider: voiceConfig?.voice_provider || 'twilio_nova_sonic'
                            });
                          }}
                          disabled={savingConfig}
                        >
                          Cancel
                        </button>
                        <button style={styles.saveButton} onClick={handleSaveConfig} disabled={savingConfig}>
                          {savingConfig ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={styles.assistantDetails}>
                      <div style={styles.detailRow}>
                        <span style={styles.detailLabel}>Provider:</span>
                        <span style={{ fontWeight: 600 }}>
                          {configFormData.voice_provider === 'nova_sonic_websocket' ? 'Nova Sonic (Browser)' : 'Twilio + Nova Sonic'}
                        </span>
                      </div>
                      {!isNovaSonic && voiceConfig?.voice_phone_number && (
                        <div style={styles.detailRow}>
                          <span style={styles.detailLabel}>Phone Number ID:</span>
                          <code style={styles.codeText}>{voiceConfig.voice_phone_number}</code>
                        </div>
                      )}
                      <div style={styles.detailRow}>
                        <span style={styles.detailLabel}>Max Daily Calls:</span>
                        <span>{voiceConfig?.voice_max_daily_calls || 100}</span>
                      </div>
                      <button style={styles.editButton} onClick={() => setEditingConfig(true)}>
                        <EditIcon /> Edit
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Per-Campaign Assistant Configuration */}
              <div style={styles.settingsSection}>
                <h3 style={styles.sectionHeader}>
                  <SparklesIcon />
                  Campaign Assistants
                </h3>
                <p style={styles.sectionDescription}>
                  Configure the AI prompt and greeting for each campaign type. Select a campaign to set up its assistant.
                </p>

                <div style={styles.assistantsList}>
                  {campaignTypes.map((type) => {
                    const assistant = getAssistantForType(type.slug);
                    const isEditing = editingAssistant === type.slug;

                    return (
                      <div key={type.slug} style={styles.assistantCard}>
                        <div style={styles.assistantCardHeader}>
                          <div>
                            <h4 style={styles.assistantName}>{type.label}</h4>
                            <span style={styles.assistantSlug}>{type.slug}</span>
                          </div>
                          {assistant ? (
                            <span style={styles.configuredBadge}>Configured</span>
                          ) : (
                            <span style={styles.notConfiguredBadge}>Not Configured</span>
                          )}
                        </div>

                        {isEditing ? (
                          <div style={styles.editForm}>
                            <div style={styles.formGroup}>
                              <label style={styles.label}>Display Name</label>
                              <input
                                type="text"
                                style={styles.input}
                                placeholder="e.g., Reactivation Assistant"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                              />
                            </div>

                            {/* The system prompt is owned by code (launch prompt v1) since the
                                July 2026 reset; every call routes there regardless of what was
                                saved here. The textarea and the Flow selector were removed on
                                2026-09-12 so nobody edits a control that does nothing. */}
                            <div style={{ ...styles.formGroup, padding: 12, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13, color: '#374151' }}>
                              <strong>How the assistant behaves</strong> is managed by Newton Health. What it knows about this practice
                              (hours, address, providers, insurances, cancellation policy) comes from the <button
                                style={{ background: 'none', border: 'none', color: '#1d4ed8', cursor: 'pointer', padding: 0, fontSize: 'inherit', fontWeight: 600, textDecoration: 'underline' }}
                                onClick={() => setSettingsTab('profile')}
                              >Practice Profile</button> tab. You can change the opening line below.
                            </div>

                            <div style={styles.formGroup}>
                              <label style={styles.label}>First Message (AI Greeting)</label>
                              <textarea
                                style={styles.textarea}
                                placeholder="Hello {{patient_first_name}}, this is {{practice_name}} calling. How are you today?"
                                value={formData.first_message}
                                onChange={(e) => setFormData({ ...formData, first_message: e.target.value })}
                                rows={3}
                              />
                              <p style={styles.helpText}>
                                The opening line the AI speaks. Supports the same placeholders.
                              </p>
                            </div>


                            <div style={styles.editActions}>
                              <button
                                style={styles.cancelButton}
                                onClick={() => setEditingAssistant(null)}
                                disabled={saving}
                              >
                                Cancel
                              </button>
                              <button style={styles.saveButton} onClick={handleSave} disabled={saving}>
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div style={styles.assistantDetails}>
                            {assistant ? (
                              <>
                                {assistant.name && (
                                  <div style={styles.detailRow}>
                                    <span style={styles.detailLabel}>Name:</span>
                                    <span>{assistant.name}</span>
                                  </div>
                                )}
                                {assistant.first_message && (
                                  <div style={styles.detailRow}>
                                    <span style={styles.detailLabel}>Greeting:</span>
                                    <span style={{ color: '#374151', fontSize: '13px' }}>
                                      {assistant.first_message.length > 100
                                        ? assistant.first_message.substring(0, 100) + '...'
                                        : assistant.first_message}
                                    </span>
                                  </div>
                                )}
                              </>
                            ) : (
                              <p style={styles.noConfigText}>
                                No assistant configured for this campaign type
                              </p>
                            )}
                            <button style={styles.editButton} onClick={() => handleEdit(type.slug)}>
                              <EditIcon />
                              {assistant ? 'Edit' : 'Configure'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* ========== INBOUND TAB ========== */}
          {settingsTab === 'inbound' && (() => {
            const inboundAssistant = getAssistantForType('inbound');

            return (
            <>
              {/* How Inbound Works */}
              <div style={styles.settingsSection}>
                <h3 style={styles.sectionHeader}>
                  <PhoneIcon />
                  Inbound Voice AI
                </h3>
                <p style={styles.sectionDescription}>
                  When a patient calls your inbound number, the AI answers, greets them, and helps with scheduling or routes to staff.
                </p>

                <div style={styles.assistantCard}>
                  <div style={{ padding: '16px 20px' }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 600, color: '#111827' }}>How It Works (Twilio + Nova Sonic)</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div style={settingsTabStyles.stepRow}>
                        <span style={settingsTabStyles.stepNum}>1</span>
                        <span style={settingsTabStyles.stepText}>Patient dials your Twilio phone number</span>
                      </div>
                      <div style={settingsTabStyles.stepRow}>
                        <span style={settingsTabStyles.stepNum}>2</span>
                        <span style={settingsTabStyles.stepText}>Twilio streams audio to our server via WebSocket (Media Streams)</span>
                      </div>
                      <div style={settingsTabStyles.stepRow}>
                        <span style={settingsTabStyles.stepNum}>3</span>
                        <span style={settingsTabStyles.stepText}>Nova Sonic AI processes speech in real-time (~1s latency) and responds</span>
                      </div>
                      <div style={settingsTabStyles.stepRow}>
                        <span style={settingsTabStyles.stepNum}>4</span>
                        <span style={settingsTabStyles.stepText}>AI helps schedule, answers questions, or routes to staff â€” call is logged with transcript</span>
                      </div>
                    </div>

                    <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                      <strong style={{ fontSize: '13px', color: '#1e40af' }}>Twilio Setup:</strong>
                      <ul style={{ margin: '8px 0 0 0', padding: '0 0 0 20px', fontSize: '13px', color: '#1e3a5f', lineHeight: '1.8' }}>
                        <li>Buy a phone number in <a href="https://console.twilio.com/us1/develop/phone-numbers" target="_blank" rel="noreferrer" style={{ color: '#1d4ed8' }}>Twilio Console</a></li>
                        <li>Set <strong>Voice webhook URL</strong> to: <code style={{ padding: '2px 6px', backgroundColor: '#dbeafe', borderRadius: '3px' }}>{`https://<your-server>/api/webhooks/twilio/inbound`}</code></li>
                        <li>Set <strong>Status callback URL</strong> to: <code style={{ padding: '2px 6px', backgroundColor: '#dbeafe', borderRadius: '3px' }}>{`https://<your-server>/api/webhooks/twilio/status`}</code></li>
                        <li>Set env vars: <code style={{ padding: '2px 6px', backgroundColor: '#dbeafe', borderRadius: '3px' }}>TWILIO_AUTH_TOKEN</code> and <code style={{ padding: '2px 6px', backgroundColor: '#dbeafe', borderRadius: '3px' }}>PUBLIC_URL</code></li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Inbound Configuration */}
              <div style={styles.settingsSection}>
                <h3 style={styles.sectionHeader}>
                  <SettingsIcon />
                  Inbound Configuration
                </h3>

                <div style={styles.assistantCard}>
                  <div style={styles.assistantCardHeader}>
                    <div>
                      <h4 style={styles.assistantName}>Inbound Phone & AI Assistant</h4>
                      <span style={styles.assistantSlug}>
                        {voiceConfig?.voice_inbound_phone
                          ? `Phone: ${voiceConfig.voice_inbound_phone}`
                          : 'No inbound phone configured'}
                      </span>
                    </div>
                    {inboundAssistant && voiceConfig?.voice_inbound_phone ? (
                      <span style={styles.configuredBadge}>Configured</span>
                    ) : (
                      <span style={styles.notConfiguredBadge}>Not Configured</span>
                    )}
                  </div>

                  {editingInbound ? (
                    <div ref={inboundFormRef} style={styles.editForm}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>
                          Twilio Phone Number
                        </label>
                        <input
                          type="text"
                          style={styles.input}
                          placeholder="e.g., +15551234567"
                          value={inboundFormData.voice_inbound_phone}
                          readOnly={!isSuperAdmin}
                          disabled={!isSuperAdmin}
                          onChange={(e) => setInboundFormData({ ...inboundFormData, voice_inbound_phone: e.target.value })}
                        />
                        <p style={styles.helpText}>
                          {isSuperAdmin
                            ? 'The Twilio phone number patients call. Enter in E.164 format (e.g., +15551234567). Must match the number configured in Twilio Console.'
                            : 'Phone assignments are managed by Newton Health via Phone Management. Contact your super-admin to change this number.'}
                        </p>
                      </div>

                      <div style={styles.formGroup}>
                        <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input
                            type="checkbox"
                            checked={inboundFormData.enabled !== false}
                            onChange={(e) => setInboundFormData({ ...inboundFormData, enabled: e.target.checked })}
                          />
                          Answer inbound calls with the AI assistant
                        </label>
                        <p style={styles.helpText}>
                          Turn this off to stop the assistant answering immediately. Callers hear a short "assistant unavailable" message and the call ends. No deploy needed.
                        </p>
                      </div>

                      <div style={styles.formGroup}>
                        <label style={styles.label}>Display Name</label>
                        <input
                          type="text"
                          style={styles.input}
                          placeholder="e.g., Front Desk AI"
                          value={inboundFormData.name}
                          onChange={(e) => setInboundFormData({ ...inboundFormData, name: e.target.value })}
                        />
                      </div>

                      {/* The system prompt is owned by code (launch prompt v1) since the
                          July 2026 reset; every call routes there regardless of what was
                          saved here. The textarea and the Flow selector were removed on
                          2026-09-12 so nobody edits a control that does nothing. */}
                      <div style={{ ...styles.formGroup, padding: 12, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13, color: '#374151' }}>
                        <strong>How the assistant behaves</strong> is managed by Newton Health. What it knows about this practice
                        (hours, address, providers, insurances, cancellation policy) comes from the <button
                          style={{ background: 'none', border: 'none', color: '#1d4ed8', cursor: 'pointer', padding: 0, fontSize: 'inherit', fontWeight: 600, textDecoration: 'underline' }}
                          onClick={() => setSettingsTab('profile')}
                        >Practice Profile</button> tab. You can change the greeting below.
                      </div>

                      <div style={styles.formGroup}>
                        <label style={styles.label}>Greeting (First Message)</label>
                        <textarea
                          style={styles.textarea}
                          placeholder="Thank you for calling {{practice_name}}! How can I help you today?"
                          value={inboundFormData.first_message}
                          onChange={(e) => setInboundFormData({ ...inboundFormData, first_message: e.target.value })}
                          rows={2}
                        />
                        <p style={styles.helpText}>
                          What the AI says when it picks up the phone.
                        </p>
                      </div>

                      <div style={styles.editActions}>
                        <button
                          style={styles.cancelButton}
                          onClick={() => setEditingInbound(false)}
                          disabled={savingInbound}
                        >
                          Cancel
                        </button>
                        <button style={styles.saveButton} onClick={handleSaveInbound} disabled={savingInbound}>
                          {savingInbound ? 'Saving...' : 'Save Inbound Config'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={styles.assistantDetails}>
                      {voiceConfig?.voice_inbound_phone && (
                        <div style={styles.detailRow}>
                          <span style={styles.detailLabel}>Phone:</span>
                          <code style={styles.codeText}>{voiceConfig.voice_inbound_phone}</code>
                        </div>
                      )}
                      {inboundAssistant ? (
                        <>
                          {inboundAssistant.name && (
                            <div style={styles.detailRow}>
                              <span style={styles.detailLabel}>Assistant:</span>
                              <span>{inboundAssistant.name}</span>
                            </div>
                          )}
                          {inboundAssistant.first_message && (
                            <div style={styles.detailRow}>
                              <span style={styles.detailLabel}>Greeting:</span>
                              <span style={{ color: '#374151', fontSize: '13px' }}>
                                {inboundAssistant.first_message.length > 100
                                  ? inboundAssistant.first_message.substring(0, 100) + '...'
                                  : inboundAssistant.first_message}
                              </span>
                            </div>
                          )}
                        </>
                      ) : (
                        <p style={styles.noConfigText}>
                          No inbound assistant configured. Set the greeting and enable it so the AI can answer calls.
                        </p>
                      )}
                      <button style={styles.editButton} onClick={handleEditInbound}>
                        <EditIcon />
                        {inboundAssistant ? 'Edit' : 'Configure'}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Tools available for inbound */}
              <div style={settingsTabStyles.infoBox}>
                <strong>Available Tools:</strong> The inbound AI has access to <code>request_callback</code>, <code>opt_out</code>, plus 15 PMS tools (validateCaller, getOpenSlots, createApptExistingPt, etc.) configured in the <button
                  style={{ background: 'none', border: 'none', color: '#1d4ed8', cursor: 'pointer', padding: 0, fontSize: 'inherit', fontWeight: 600, textDecoration: 'underline' }}
                  onClick={() => setSettingsTab('tools')}
                >Tools tab</button>.
              </div>
            </>
            );
          })()}

          {/* ========== PRACTICE PROFILE TAB ========== */}
          {settingsTab === 'profile' && (
            <ProfileTab practiceId={practiceId} />
          )}

          {/* ========== TOOLS TAB ========== */}
          {settingsTab === 'tools' && (
            <>
              {/* Built-in Tools */}
              <div style={styles.settingsSection}>
                <h3 style={styles.sectionHeader}>
                  <SettingsIcon />
                  Built-in Tools
                </h3>
                <p style={styles.sectionDescription}>
                  These tools are always available to the AI during voice calls. No configuration needed.
                </p>

                {[
                  { name: 'request_callback', desc: 'Patient wants to speak to a human. Creates a callback task with reason and urgency.' },
                  { name: 'opt_out', desc: 'Patient explicitly says "stop calling me." Marks them as opted-out from voice calls.' },
                  { name: 'getBookingOptions', desc: 'Looks up valid appointment types, reason codes, and booking configs for this practice. Use to verify correct values before booking or rescheduling.' }
                ].map(t => (
                  <div key={t.name} style={{ ...styles.assistantCard, marginTop: '8px' }}>
                    <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <code style={{ padding: '2px 8px', backgroundColor: '#f3f4f6', borderRadius: '4px', fontSize: '13px', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{t.name}</code>
                      <span style={{ fontSize: '13px', color: '#6b7280' }}>{t.desc}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Nova Sonic Session Configuration */}
              <div style={styles.settingsSection}>
                <h3 style={styles.sectionHeader}>
                  <SettingsIcon />
                  Nova Sonic Configuration
                </h3>
                <p style={styles.sectionDescription}>
                  Configure the AI voice model behavior: response latency, voice selection, and generation parameters.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Response Sensitivity</label>
                    <select
                      style={styles.input}
                      value={configFormData.endpointing_sensitivity}
                      onChange={(e) => setConfigFormData({ ...configFormData, endpointing_sensitivity: e.target.value })}
                    >
                      <option value="HIGH">HIGH â€” Fast (1.5s pause, may interrupt)</option>
                      <option value="MEDIUM">MEDIUM â€” Balanced (1.75s pause)</option>
                      <option value="LOW">LOW â€” Patient (2s pause, fewer interruptions)</option>
                    </select>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Voice</label>
                    <select
                      style={styles.input}
                      value={configFormData.voice_id}
                      onChange={(e) => setConfigFormData({ ...configFormData, voice_id: e.target.value })}
                    >
                      <optgroup label="English (US) â€” Polyglot">
                        <option value="tiffany">Tiffany (Female)</option>
                        <option value="matthew">Matthew (Male)</option>
                      </optgroup>
                      <optgroup label="English (UK)">
                        <option value="amy">Amy (Female)</option>
                      </optgroup>
                      <optgroup label="English (AU)">
                        <option value="olivia">Olivia (Female)</option>
                      </optgroup>
                      <optgroup label="English (IN)">
                        <option value="kiara">Kiara (Female)</option>
                        <option value="arjun">Arjun (Male)</option>
                      </optgroup>
                      <optgroup label="Spanish (US)">
                        <option value="lupe">Lupe (Female)</option>
                        <option value="carlos">Carlos (Male)</option>
                      </optgroup>
                      <optgroup label="French">
                        <option value="ambre">Ambre (Female)</option>
                        <option value="florian">Florian (Male)</option>
                      </optgroup>
                      <optgroup label="German">
                        <option value="tina">Tina (Female)</option>
                        <option value="lennart">Lennart (Male)</option>
                      </optgroup>
                      <optgroup label="Italian">
                        <option value="beatrice">Beatrice (Female)</option>
                        <option value="lorenzo">Lorenzo (Male)</option>
                      </optgroup>
                      <optgroup label="Portuguese (BR)">
                        <option value="carolina">Carolina (Female)</option>
                        <option value="leo">Leo (Male)</option>
                      </optgroup>
                    </select>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Temperature ({configFormData.temperature})</label>
                    <input
                      type="range" min="0" max="1" step="0.1"
                      style={{ ...styles.input, padding: '8px 4px' }}
                      value={configFormData.temperature}
                      onChange={(e) => setConfigFormData({ ...configFormData, temperature: parseFloat(e.target.value) })}
                    />
                    <span style={{ fontSize: '11px', color: '#9ca3af' }}>Lower = more focused, Higher = more creative</span>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Max Tokens</label>
                    <input
                      type="number" style={styles.input}
                      min="256" max="4096" step="256"
                      value={configFormData.max_tokens}
                      onChange={(e) => setConfigFormData({ ...configFormData, max_tokens: parseInt(e.target.value, 10) })}
                    />
                    <span style={{ fontSize: '11px', color: '#9ca3af' }}>Maximum response length (default 1024)</span>
                  </div>
                </div>
                <button
                  style={{ ...styles.saveButton, marginTop: '8px' }}
                  onClick={handleSaveWebhookConfig}
                  disabled={savingWebhookConfig}
                >
                  {savingWebhookConfig ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>

              {/* Load Default PMS Tools */}
              {voiceTools.length === 0 && (
                <div style={styles.settingsSection}>
                  <h3 style={styles.sectionHeader}>
                    <SettingsIcon />
                    Load Default PMS Tools
                  </h3>
                  <p style={styles.sectionDescription}>
                    Load 15 pre-configured tool definitions for your Practice Management System: patient validation, appointment scheduling, rescheduling, cancellation, insurance management, and more. Tool calls are routed through the webhook URL above.
                  </p>
                  <div style={{ ...settingsTabStyles.infoBox, marginTop: '8px', marginBottom: '12px' }}>
                    <strong>Tools:</strong> validate_caller, get_patient_details, get_providers_list, get_open_slots, get_open_slots_by_provider, get_earliest_slot, get_earliest_slot_by_provider, create_appt_existing_pt, create_appt_new_pt, get_appt_details, get_active_patient_appt, reschedule_appt, cancel_appt, update_insurance, add_insurance
                  </div>
                  <button
                    style={styles.newCallButton}
                    onClick={handleSeedDefaults}
                    disabled={seedingTools}
                  >
                    {seedingTools ? 'Loading...' : 'Load 15 Default PMS Tools'}
                  </button>
                </div>
              )}

              {/* Custom HTTP Tools */}
              <div style={styles.settingsSection}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 style={styles.sectionHeader}>
                      <SettingsIcon />
                      Custom Tools (HTTP)
                    </h3>
                    <p style={styles.sectionDescription}>
                      Add external API endpoints the AI can call during conversations â€” e.g., check appointment slots, look up insurance, verify patient info.
                    </p>
                  </div>
                  <button style={styles.newCallButton} onClick={handleNewTool}>
                    + Add Tool
                  </button>
                </div>

                {/* Tool editing form */}
                {editingTool && (
                  <div ref={toolFormRef} style={{ ...styles.assistantCard, marginTop: '16px' }}>
                    <div style={styles.editForm}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div style={styles.formGroup}>
                          <label style={styles.label}>Tool Name <span style={styles.required}>*</span></label>
                          <input
                            type="text" style={styles.input} placeholder="e.g., check_availability"
                            value={toolFormData.name}
                            onChange={(e) => setToolFormData({ ...toolFormData, name: e.target.value })}
                          />
                          <p style={styles.helpText}>snake_case, no spaces. This is how the AI references the tool.</p>
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.label}>HTTP Method</label>
                          <select
                            style={styles.input}
                            value={toolFormData.http_method}
                            onChange={(e) => setToolFormData({ ...toolFormData, http_method: e.target.value })}
                          >
                            <option value="GET">GET</option>
                            <option value="POST">POST</option>
                            <option value="PUT">PUT</option>
                          </select>
                        </div>
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Description <span style={styles.required}>*</span></label>
                        <textarea
                          style={styles.textarea} rows={2}
                          placeholder="Check available appointment slots for a given date. Returns list of open time slots."
                          value={toolFormData.description}
                          onChange={(e) => setToolFormData({ ...toolFormData, description: e.target.value })}
                        />
                        <p style={styles.helpText}>The AI reads this to decide when to call the tool. Be specific.</p>
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>URL Template <span style={styles.required}>*</span></label>
                        <input
                          type="text" style={styles.input}
                          placeholder="https://api.example.com/appointments?date={{date}}"
                          value={toolFormData.url_template}
                          onChange={(e) => setToolFormData({ ...toolFormData, url_template: e.target.value })}
                        />
                        <p style={styles.helpText}>Use <code>{'{{param}}'}</code> placeholders for dynamic values from the AI.</p>
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Parameters (JSON array)</label>
                        <textarea
                          style={{ ...styles.textarea, fontFamily: 'monospace', fontSize: '12px' }} rows={3}
                          placeholder={'[\n  {"name": "date", "type": "string", "description": "Date to check (YYYY-MM-DD)", "required": true}\n]'}
                          value={toolFormData.parameters}
                          onChange={(e) => setToolFormData({ ...toolFormData, parameters: e.target.value })}
                        />
                        <p style={styles.helpText}>Define what parameters the AI should provide. These become the tool's input schema.</p>
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Headers (JSON)</label>
                        <input
                          type="text" style={styles.input}
                          placeholder='{"Authorization": "Bearer xxx", "Content-Type": "application/json"}'
                          value={toolFormData.headers}
                          onChange={(e) => setToolFormData({ ...toolFormData, headers: e.target.value })}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Body Template</label>
                        <textarea
                          style={{ ...styles.textarea, fontFamily: 'monospace', fontSize: '12px' }} rows={2}
                          placeholder='{"date": "{{date}}", "practice_id": "{{practice_id}}"}'
                          value={toolFormData.body_template}
                          onChange={(e) => setToolFormData({ ...toolFormData, body_template: e.target.value })}
                        />
                        <p style={styles.helpText}>For POST/PUT requests. Use <code>{'{{param}}'}</code> placeholders.</p>
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Response Mapping (dot-path)</label>
                        <input
                          type="text" style={styles.input}
                          placeholder="data.available_slots"
                          value={toolFormData.response_mapping}
                          onChange={(e) => setToolFormData({ ...toolFormData, response_mapping: e.target.value })}
                        />
                        <p style={styles.helpText}>Extract a nested field from the API response to pass back to the AI.</p>
                      </div>
                      <div style={styles.editActions}>
                        <button style={styles.cancelButton} onClick={() => setEditingTool(null)} disabled={savingTool}>
                          Cancel
                        </button>
                        <button style={styles.saveButton} onClick={handleSaveTool} disabled={savingTool}>
                          {savingTool ? 'Saving...' : 'Save Tool'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tools list */}
                {voiceTools.length === 0 && !editingTool && (
                  <p style={{ ...styles.noConfigText, marginTop: '12px' }}>
                    No custom tools configured yet. Click "Add Tool" to let the AI call external APIs during voice calls.
                  </p>
                )}
                {voiceTools.map((tool) => (
                  <div key={tool.id} style={{ ...styles.assistantCard, marginTop: '12px' }}>
                    <div style={styles.assistantCardHeader}>
                      <div>
                        <h4 style={styles.assistantName}>{tool.name}</h4>
                        <span style={styles.assistantSlug}>
                          {tool.http_method} {tool.url_template?.substring(0, 60)}{tool.url_template?.length > 60 ? '...' : ''}
                        </span>
                      </div>
                      <span style={tool.enabled ? styles.configuredBadge : styles.notConfiguredBadge}>
                        {tool.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <div style={styles.assistantDetails}>
                      <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#6b7280' }}>{tool.description}</p>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button style={styles.editButton} onClick={() => handleEditTool(tool)}>
                          <EditIcon /> Edit
                        </button>
                        <button
                          style={{ ...styles.editButton, color: '#dc2626' }}
                          onClick={() => handleDeleteTool(tool.id)}
                        >
                          <CloseIcon /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Tool Usage Tip */}
              <div style={settingsTabStyles.infoBox}>
                <strong>Tip:</strong> After loading default PMS tools, reference them in your assistant's System Prompt so the AI knows when to use them.
                For example: <code>- validateCaller: Always call first to identify the patient. Then use getOpenSlots and createApptExistingPt to book.</code>
              </div>
            </>
          )}



        </div>
      )}
    </div>
  );
}

// Settings tab styles
const settingsTabStyles = {
  tabBar: {
    display: 'flex',
    gap: '0',
    borderBottom: '2px solid #e5e7eb',
    marginBottom: '24px'
  },
  tab: {
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#6b7280',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    marginBottom: '-2px',
    cursor: 'pointer',
    transition: 'color 0.15s, border-color 0.15s'
  },
  tabActive: {
    color: '#3b82f6',
    fontWeight: 600,
    borderBottomColor: '#3b82f6'
  },
  infoBox: {
    padding: '12px 16px',
    backgroundColor: '#eff6ff',
    borderRadius: '8px',
    fontSize: '13px',
    lineHeight: '1.6',
    color: '#1d4ed8'
  },
  stepRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px'
  },
  stepNum: {
    flexShrink: 0,
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 700
  },
  stepText: {
    fontSize: '14px',
    color: '#374151',
    lineHeight: '1.5',
    paddingTop: '2px'
  }
};


// New Call Modal Component
function NewCallModal({ practiceId, onClose, onInitiateCall }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [campaignType, setCampaignType] = useState('');
  const [searching, setSearching] = useState(false);
  const [initiating, setInitiating] = useState(false);
  const [error, setError] = useState(null);
  const [patientConsent, setPatientConsent] = useState(null);

  // Load campaign types from the practice's actual CampaignType rows (same
  // shape as Voice AI Settings). Custom slugs need to show up here too —
  // hardcoding the five seeded defaults blocked test campaigns from being
  // picked at the New Call step.
  const [campaignTypes, setCampaignTypes] = useState([]);
  useEffect(() => {
    if (!practiceId) return;
    api.getCampaignTypes(practiceId, true)
      .then(res => {
        const rows = (res.data || []).filter(t => t.slug)
          .map(t => ({ slug: t.slug, label: t.name || t.slug }));
        setCampaignTypes(rows.length > 0 ? rows : [
          { slug: 'reactivation', label: 'Reactivation Campaign' },
          { slug: 'no-show', label: 'No-Show Follow-up' },
          { slug: 'cancellation', label: 'Cancellation Recovery' },
          { slug: 'treatment-followup', label: 'Treatment Follow-up' },
          { slug: 'recall', label: 'Recall/Hygiene' }
        ]);
      })
      .catch(() => {
        setCampaignTypes([
          { slug: 'reactivation', label: 'Reactivation Campaign' },
          { slug: 'no-show', label: 'No-Show Follow-up' },
          { slug: 'cancellation', label: 'Cancellation Recovery' },
          { slug: 'treatment-followup', label: 'Treatment Follow-up' },
          { slug: 'recall', label: 'Recall/Hygiene' }
        ]);
      });
  }, [practiceId]);

  // Search patients
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    try {
      setSearching(true);
      setError(null);
      const response = await api.getPatients(practiceId, {
        search: searchQuery.trim(),
        limit: 10
      });
      setPatients(response.data || []);
      if (response.data?.length === 0) {
        setError('No patients found matching your search');
      }
    } catch (err) {
      console.error('Error searching patients:', err);
      setError('Failed to search patients');
    } finally {
      setSearching(false);
    }
  };

  // Handle patient selection
  const handleSelectPatient = async (patient) => {
    setSelectedPatient(patient);
    setPatients([]);
    setSearchQuery('');

    // Load consent status
    try {
      const response = await api.getPatientVoiceConsent(practiceId, patient.id);
      setPatientConsent(response.data);
    } catch (err) {
      console.error('Error loading consent:', err);
      setPatientConsent({ voice_consent: 'UNKNOWN' });
    }
  };

  // Handle call initiation
  const handleInitiate = async () => {
    if (!selectedPatient || !campaignType) return;

    if (patientConsent?.voice_consent === 'OPT_OUT') {
      setError('Cannot call patient who has opted out of voice calls');
      return;
    }

    try {
      setInitiating(true);
      setError(null);
      await onInitiateCall(selectedPatient.id, campaignType, selectedPatient);
    } catch (err) {
      setError(err.message || 'Failed to initiate call');
      setInitiating(false);
    }
  };

  // Handle Enter key in search
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.newCallModal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>
            <PhonePlusIcon />
            New Voice Call
          </h3>
          <button style={styles.modalClose} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div style={styles.modalBody}>
          {error && (
            <div style={styles.modalError}>
              <AlertIcon />
              <span>{error}</span>
              <button onClick={() => setError(null)} style={styles.modalErrorClose}>
                <CloseIcon />
              </button>
            </div>
          )}

          {/* Patient Search */}
          {!selectedPatient ? (
            <div style={styles.searchSection}>
              <label style={styles.label}>Search Patient</label>
              <div style={styles.searchRow}>
                <input
                  type="text"
                  style={styles.searchInput}
                  placeholder="Search by name, phone, or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  autoFocus
                />
                <button
                  style={styles.searchButton}
                  onClick={handleSearch}
                  disabled={searching || !searchQuery.trim()}
                >
                  {searching ? <LoadingSpinner /> : <SearchIcon />}
                </button>
              </div>

              {/* Search Results */}
              {patients.length > 0 && (
                <div style={styles.searchResults}>
                  {patients.map((patient) => (
                    <button
                      key={patient.id}
                      style={styles.patientResult}
                      onClick={() => handleSelectPatient(patient)}
                    >
                      <div style={styles.patientResultAvatar}>
                        {getInitials(`${patient.first_name} ${patient.last_name}`)}
                      </div>
                      <div style={styles.patientResultInfo}>
                        <span style={styles.patientResultName}>
                          {patient.first_name} {patient.last_name}
                        </span>
                        <span style={styles.patientResultDetails}>
                          {patient.phone || patient.email || 'No contact info'}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Selected Patient */}
              <div style={styles.selectedPatientCard}>
                <div style={styles.selectedPatientInfo}>
                  <div style={styles.patientAvatarLarge}>
                    {getInitials(`${selectedPatient.first_name} ${selectedPatient.last_name}`)}
                  </div>
                  <div>
                    <h4 style={styles.selectedPatientName}>
                      {selectedPatient.first_name} {selectedPatient.last_name}
                    </h4>
                    <p style={styles.selectedPatientPhone}>
                      {selectedPatient.phone || 'No phone number'}
                    </p>
                  </div>
                </div>
                <button
                  style={styles.changePatientButton}
                  onClick={() => {
                    setSelectedPatient(null);
                    setPatientConsent(null);
                    setCampaignType('');
                  }}
                >
                  Change
                </button>
              </div>

              {/* Consent Status */}
              {patientConsent && (
                <div style={{
                  ...styles.consentStatus,
                  ...(patientConsent.voice_consent === 'OPT_OUT' ? styles.consentOptOut :
                      patientConsent.voice_consent === 'OPT_IN' ? styles.consentOptIn :
                      styles.consentUnknown)
                }}>
                  {patientConsent.voice_consent === 'OPT_OUT' ? (
                    <>
                      <AlertIcon />
                      <span>Patient has opted out of voice calls</span>
                    </>
                  ) : patientConsent.voice_consent === 'OPT_IN' ? (
                    <>
                      <CheckCircleIcon />
                      <span>Patient has consented to voice calls</span>
                    </>
                  ) : (
                    <>
                      <InfoIcon />
                      <span>Voice consent status unknown</span>
                    </>
                  )}
                </div>
              )}

              {/* Phone Number Check */}
              {!selectedPatient.phone && (
                <div style={styles.warningBox}>
                  <AlertIcon />
                  <span>This patient does not have a phone number on file</span>
                </div>
              )}

              {/* Campaign Type Selection */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Campaign Type</label>
                <select
                  style={styles.selectInput}
                  value={campaignType}
                  onChange={(e) => setCampaignType(e.target.value)}
                  disabled={!selectedPatient.phone || patientConsent?.voice_consent === 'OPT_OUT'}
                >
                  <option value="">Select a campaign type...</option>
                  {campaignTypes.map((type) => (
                    <option key={type.slug} value={type.slug}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <p style={styles.helpText}>
                  The AI assistant configured for this campaign type will be used for the call
                </p>
              </div>
            </>
          )}
        </div>

        <div style={styles.modalFooter}>
          <button style={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button
            style={{
              ...styles.initiateCallButton,
              ...((!selectedPatient || !campaignType || !selectedPatient.phone ||
                   patientConsent?.voice_consent === 'OPT_OUT' || initiating)
                   ? styles.initiateCallButtonDisabled : {})
            }}
            onClick={handleInitiate}
            disabled={!selectedPatient || !campaignType || !selectedPatient.phone ||
                     patientConsent?.voice_consent === 'OPT_OUT' || initiating}
          >
            {initiating ? (
              <>
                <LoadingSpinner />
                Initiating...
              </>
            ) : (
              <>
                <PhoneIcon />
                Initiate Call
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Voice Session Panel â€” live voice AI call UI
function VoiceSessionPanel({ practiceId, patientId, campaignType, patient, onEnd }) {
  const [status, setStatus] = useState('connecting'); // connecting, ready, listening, error
  const [transcript, setTranscript] = useState([]);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [toolEvents, setToolEvents] = useState([]);
  const [errorMsg, setErrorMsg] = useState(null);

  const audioEngineRef = useRef(null);
  const socketClientRef = useRef(null);
  const timerRef = useRef(null);
  const transcriptEndRef = useRef(null);

  // Auto-scroll transcript
  useEffect(() => {
    if (transcriptEndRef.current) {
      transcriptEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [transcript, toolEvents]);

  // Duration timer
  useEffect(() => {
    if (status === 'listening') {
      timerRef.current = setInterval(() => {
        setDuration(d => d + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [status]);

  // Initialize voice session on mount
  useEffect(() => {
    const engine = new VoiceAudioEngine();
    const client = new VoiceSocketClient();
    audioEngineRef.current = engine;
    socketClientRef.current = client;

    // Get auth token
    const token = localStorage.getItem('nh_auth_token');

    // Set up event handlers before connecting
    client.on('connect', () => {
      console.log('[VoiceSession] Socket connected, initializing...');
      client.initializeConnection({ practiceId, patientId, campaignType });
    });

    client.on('audioReady', () => {
      console.log('[VoiceSession] Audio ready, starting prompt...');
      setStatus('ready');
      client.promptStart();
    });

    client.on('audioOutput', (data) => {
      engine.playAudioChunk(data.audio);
    });

    client.on('textOutput', (data) => {
      setTranscript(prev => [...prev, { role: data.role, text: data.text }]);
    });

    client.on('toolUse', (data) => {
      setToolEvents(prev => [...prev, { type: 'call', name: data.toolName, params: data.params, time: new Date() }]);
    });

    client.on('toolResult', (data) => {
      setToolEvents(prev => [...prev, { type: 'result', name: data.toolName, result: data.result, time: new Date() }]);
    });

    client.on('sessionClosed', () => {
      console.log('[VoiceSession] Session closed by server');
      cleanup();
      onEnd();
    });

    client.on('error', (data) => {
      console.error('[VoiceSession] Error:', data);
      setErrorMsg(data.message || 'Voice session error');
      setStatus('error');
    });

    client.on('connect_error', (err) => {
      console.error('[VoiceSession] Connection error:', err);
      setErrorMsg('Failed to connect to voice server');
      setStatus('error');
    });

    // Connect
    client.connect({ token });

    // Start mic capture after a short delay to let socket connect
    const startMic = async () => {
      try {
        await engine.startCapture((base64Audio) => {
          if (client.isConnected) {
            client.sendAudio(base64Audio);
          }
        });
        setStatus('listening');
        client.startAudio();
      } catch (err) {
        console.error('[VoiceSession] Mic access error:', err);
        setErrorMsg('Microphone access denied. Please allow microphone access and try again.');
        setStatus('error');
      }
    };

    // Wait for audioReady before starting mic
    const origHandler = client.handlers.audioReady;
    client.on('audioReady', (data) => {
      if (origHandler) origHandler(data);
      startMic();
    });

    return () => cleanup();

    function cleanup() {
      if (timerRef.current) clearInterval(timerRef.current);
      engine.destroy();
      client.endSession();
      client.disconnect();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMuteToggle = () => {
    if (audioEngineRef.current) {
      const muted = audioEngineRef.current.toggleMute();
      setIsMuted(muted);
    }
  };

  const handleEndSession = () => {
    if (socketClientRef.current) {
      socketClientRef.current.endSession();
      socketClientRef.current.disconnect();
    }
    if (audioEngineRef.current) {
      audioEngineRef.current.destroy();
    }
    if (timerRef.current) clearInterval(timerRef.current);
    onEnd();
  };

  const patientName = patient
    ? `${patient.first_name || ''} ${patient.last_name || ''}`.trim()
    : 'Patient';

  return (
    <div style={voiceStyles.container}>
      {/* Header */}
      <div style={voiceStyles.header}>
        <button style={voiceStyles.backButton} onClick={handleEndSession}>
          <CloseIcon /> Back to Calls
        </button>
        <div style={voiceStyles.headerCenter}>
          <div style={{
            ...voiceStyles.statusDot,
            backgroundColor: status === 'listening' ? '#10b981' : status === 'error' ? '#ef4444' : '#f59e0b'
          }} />
          <span style={voiceStyles.statusText}>
            {status === 'connecting' && 'Connecting...'}
            {status === 'ready' && 'Starting session...'}
            {status === 'listening' && 'Live'}
            {status === 'error' && 'Error'}
          </span>
          <span style={voiceStyles.durationText}>
            {formatDuration(duration)}
          </span>
        </div>
        <div style={{ width: '120px' }} />
      </div>

      {/* Patient Info */}
      <div style={voiceStyles.patientBar}>
        <div style={voiceStyles.patientAvatar}>
          {getInitials(patientName)}
        </div>
        <div>
          <div style={voiceStyles.patientName}>{patientName}</div>
          <div style={voiceStyles.patientMeta}>
            {patient?.phone || 'No phone'} &middot; {campaignType}
          </div>
        </div>
      </div>

      {/* Error banner */}
      {errorMsg && (
        <div style={voiceStyles.errorBanner}>
          <AlertIcon />
          <span>{errorMsg}</span>
          <button onClick={() => { setErrorMsg(null); }} style={voiceStyles.errorClose}>
            <CloseIcon />
          </button>
        </div>
      )}

      {/* Transcript + Tool Events */}
      <div style={voiceStyles.transcriptArea}>
        {transcript.length === 0 && status !== 'error' && (
          <div style={voiceStyles.waitingMessage}>
            {status === 'listening'
              ? 'AI is starting the conversation...'
              : 'Setting up voice session...'}
          </div>
        )}
        {transcript.map((entry, i) => (
          <div key={i} style={{
            ...voiceStyles.transcriptEntry,
            ...(entry.role === 'ASSISTANT' ? voiceStyles.transcriptAI : voiceStyles.transcriptUser)
          }}>
            <span style={voiceStyles.transcriptRole}>
              {entry.role === 'ASSISTANT' ? 'AI' : 'Patient'}
            </span>
            <span style={voiceStyles.transcriptText}>{entry.text}</span>
          </div>
        ))}
        {toolEvents.map((evt, i) => (
          <div key={`tool-${i}`} style={voiceStyles.toolEvent}>
            <SparklesIcon />
            <span>
              {evt.type === 'call'
                ? `Tool called: ${evt.name}(${JSON.stringify(evt.params).substring(0, 80)})`
                : `Tool result: ${evt.name} â†’ ${typeof evt.result === 'string' ? evt.result.substring(0, 100) : JSON.stringify(evt.result).substring(0, 100)}`}
            </span>
          </div>
        ))}
        <div ref={transcriptEndRef} />
      </div>

      {/* Controls */}
      <div style={voiceStyles.controls}>
        <button
          style={{
            ...voiceStyles.controlButton,
            ...(isMuted ? voiceStyles.controlButtonMuted : {})
          }}
          onClick={handleMuteToggle}
          disabled={status !== 'listening'}
        >
          {isMuted ? <MicOffIcon /> : <MicIcon />}
          {isMuted ? 'Unmute' : 'Mute'}
        </button>
        <button
          style={voiceStyles.endButton}
          onClick={handleEndSession}
        >
          <PhoneOffIcon />
          End Session
        </button>
      </div>
    </div>
  );
}

// Mic icons
function MicIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

function MicOffIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
      <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .25-.02.5-.04.74" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

// Voice session panel styles
const voiceStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 120px)',
    backgroundColor: '#f3f4f6',
    margin: '-20px -24px',
    width: 'calc(100% + 48px)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 24px',
    backgroundColor: '#fff',
    borderBottom: '1px solid #e5e7eb'
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
    cursor: 'pointer'
  },
  headerCenter: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  statusDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%'
  },
  statusText: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#111827'
  },
  durationText: {
    fontSize: '14px',
    color: '#6b7280',
    fontVariantNumeric: 'tabular-nums'
  },
  patientBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '14px 24px',
    backgroundColor: '#fff',
    borderBottom: '1px solid #e5e7eb'
  },
  patientAvatar: {
    width: '42px',
    height: '42px',
    borderRadius: '50%',
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '15px',
    fontWeight: '600',
    flexShrink: 0
  },
  patientName: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#111827'
  },
  patientMeta: {
    fontSize: '13px',
    color: '#6b7280',
    marginTop: '2px'
  },
  errorBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 24px',
    backgroundColor: '#fef2f2',
    borderBottom: '1px solid #fecaca',
    color: '#dc2626',
    fontSize: '14px'
  },
  errorClose: {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    color: '#dc2626',
    display: 'flex'
  },
  transcriptArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  waitingMessage: {
    textAlign: 'center',
    color: '#9ca3af',
    fontSize: '14px',
    padding: '40px 0'
  },
  transcriptEntry: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '12px 16px',
    borderRadius: '10px',
    maxWidth: '80%'
  },
  transcriptAI: {
    backgroundColor: '#eff6ff',
    alignSelf: 'flex-start',
    borderBottomLeftRadius: '2px'
  },
  transcriptUser: {
    backgroundColor: '#f0fdf4',
    alignSelf: 'flex-end',
    borderBottomRightRadius: '2px'
  },
  transcriptRole: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  transcriptText: {
    fontSize: '14px',
    lineHeight: '1.5',
    color: '#111827'
  },
  toolEvent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 14px',
    backgroundColor: '#fefce8',
    borderRadius: '8px',
    fontSize: '12px',
    color: '#854d0e',
    alignSelf: 'center',
    maxWidth: '90%'
  },
  controls: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    padding: '20px 24px',
    backgroundColor: '#fff',
    borderTop: '1px solid #e5e7eb'
  },
  controlButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: '#f3f4f6',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    cursor: 'pointer'
  },
  controlButtonMuted: {
    backgroundColor: '#fef2f2',
    borderColor: '#fecaca',
    color: '#dc2626'
  },
  endButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: '#ef4444',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  }
};

// Sub-components
function StatCard({ icon, label, value, color, highlight }) {
  return (
    <div style={{
      ...styles.statCard,
      ...(highlight ? { borderColor: color, borderWidth: '2px' } : {})
    }}>
      <div style={{ ...styles.statIcon, backgroundColor: `${color}15`, color }}>
        {icon}
      </div>
      <div style={styles.statInfo}>
        <span style={styles.statValue}>{value}</span>
        <span style={styles.statLabel}>{label}</span>
      </div>
    </div>
  );
}

function StatusBadge({ status, outcome, large }) {
  const getStatusStyle = () => {
    if (outcome === 'booked') return styles.badgeSuccess;
    if (outcome === 'interested') return styles.badgePurple;
    if (outcome === 'callback_requested') return styles.badgeWarning;
    if (outcome === 'opt_out' || outcome === 'declined') return styles.badgeDanger;
    if (status === 'answered') return styles.badgeInfo;
    if (status === 'voicemail') return styles.badgeGray;
    return styles.badgeGray;
  };

  const getText = () => {
    if (outcome) return formatOutcome(outcome);
    return status || 'Unknown';
  };

  return (
    <span style={{
      ...styles.statusBadge,
      ...getStatusStyle(),
      ...(large ? styles.statusBadgeLarge : {})
    }}>
      {getText()}
    </span>
  );
}

function UrgencyBadge({ urgency }) {
  const getStyle = () => {
    if (urgency === 'high') return styles.urgencyHigh;
    if (urgency === 'medium') return styles.urgencyMedium;
    return styles.urgencyLow;
  };

  return (
    <span style={{ ...styles.urgencyBadge, ...getStyle() }}>
      {urgency}
    </span>
  );
}

function InfoCard({ label, value, icon }) {
  return (
    <div style={styles.infoCard}>
      <div style={styles.infoCardIcon}>{icon}</div>
      <div style={styles.infoCardContent}>
        <span style={styles.infoCardLabel}>{label}</span>
        <span style={styles.infoCardValue}>{value}</span>
      </div>
    </div>
  );
}

// Utility functions
function getInitials(name) {
  if (!name) return '?';
  const parts = name.split(' ');
  const first = parts[0]?.charAt(0).toUpperCase() || '';
  const last = parts[1]?.charAt(0).toUpperCase() || '';
  return first + last || '?';
}

function formatDuration(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// GH-180: Recent Calls row now shows absolute wall-clock start → end times
// plus the duration in parens. If end_time isn't populated but duration is,
// synthesize the end from start + duration so staff still see a range.
function formatCallTimeRange(startedAt, endedAt, durationSeconds) {
  if (!startedAt) return '—';
  try {
    const start = new Date(startedAt);
    let end = endedAt ? new Date(endedAt) : null;
    if (!end && durationSeconds) {
      end = new Date(start.getTime() + durationSeconds * 1000);
    }
    const timeOpts = { hour: '2-digit', minute: '2-digit' };
    const startStr = start.toLocaleTimeString([], timeOpts);
    if (!end || isNaN(end.getTime())) return startStr;
    const endStr = end.toLocaleTimeString([], timeOpts);
    const dur = durationSeconds ? ` (${formatDuration(durationSeconds)})` : '';
    return `${startStr} → ${endStr}${dur}`;
  } catch { return startedAt; }
}

function formatRelativeTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function formatDateTime(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString();
}

/**
 * ProfileTab — Practice Profile sub-tab inside Voice AI Settings.
 *
 * Everything the inbound AI reads back to callers lives here: hours,
 * address, office phone, cancellation policy, accepted insurances, plus
 * the emergency alert contact list, FAQs and holidays. A readiness
 * checklist at the top mirrors PracticeVoiceProfile.readiness() on the
 * server. Sections a–g save together via one PUT /voice/profile; FAQs and
 * holidays save per row.
 */
const PROFILE_DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' }
];
const EMPTY_HOURS_DAY = { closed: true, open: '08:00', close: '17:00' };
const EMPTY_ADDRESS = { street: '', suite: '', city: '', state: '', zip: '', country: '' };

// "8:00 AM - 5:00 PM" → { open: "08:00", close: "17:00" }. Older rows (QA
// practice 8) store hours as strings; the editor normalizes them to the
// { open, close } object shape on save.
function parseHoursString(str) {
  const m = String(str).match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s*[-–]\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
  if (!m) return null;
  const to24 = (h, min, ap) => {
    let hh = parseInt(h, 10);
    if (ap) {
      const p = ap.toLowerCase();
      if (p === 'pm' && hh < 12) hh += 12;
      if (p === 'am' && hh === 12) hh = 0;
    }
    if (hh > 23) return null;
    return `${String(hh).padStart(2, '0')}:${min || '00'}`;
  };
  const open = to24(m[1], m[2], m[3]);
  const close = to24(m[4], m[5], m[6]);
  return open && close ? { open, close } : null;
}

function hoursFromProfile(hoursJson) {
  const out = {};
  for (const { key } of PROFILE_DAYS) {
    const v = hoursJson && hoursJson[key];
    if (v && typeof v === 'object' && v.open && v.close) {
      out[key] = { closed: false, open: v.open, close: v.close };
    } else if (typeof v === 'string' && !/closed/i.test(v)) {
      const parsed = parseHoursString(v);
      out[key] = parsed ? { closed: false, ...parsed } : { ...EMPTY_HOURS_DAY };
    } else {
      out[key] = { ...EMPTY_HOURS_DAY };
    }
  }
  return out;
}

function ProfileTab({ practiceId }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [loading, setLoading] = useState(true);
  const [capabilities, setCapabilities] = useState([]);
  const [savingCaps, setSavingCaps] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [readiness, setReadiness] = useState([]);
  const [checkingReadiness, setCheckingReadiness] = useState(false);

  const [hours, setHours] = useState(() => hoursFromProfile(null));
  const [address, setAddress] = useState({ ...EMPTY_ADDRESS });
  const [officePhone, setOfficePhone] = useState('');
  const [cancellationWindow, setCancellationWindow] = useState(24);
  const [lateMessage, setLateMessage] = useState('');
  const [insurances, setInsurances] = useState([]);
  const [newInsurance, setNewInsurance] = useState('');
  const [contacts, setContacts] = useState([]);

  const [faqs, setFaqs] = useState([]);
  const [holidays, setHolidays] = useState([]);

  const showError = useCallback((message) => { setError(message); setSuccess(null); }, []);
  const showSuccess = useCallback((message) => { setSuccess(message); setError(null); }, []);

  const applyProfile = useCallback((profile) => {
    if (!profile) return;
    setHours(hoursFromProfile(profile.hours_json));
    setAddress({ ...EMPTY_ADDRESS, ...(profile.address_json || {}) });
    setOfficePhone(profile.office_phone || '');
    setCancellationWindow(profile.cancellation_window_hours ?? 24);
    setLateMessage(profile.cancellation_late_flag_message || '');
    setInsurances((profile.accepted_insurances || []).map(i => (
      typeof i === 'string' ? { name: i, short: '' } : { name: i?.name || '', short: i?.short || '' }
    )).filter(i => i.name));
    setContacts((profile.emergency_alert_contacts || []).map(c => ({
      method: c?.method === 'email' ? 'email' : 'sms',
      destination: c?.destination || '',
      label: c?.label || ''
    })));
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [profileRes, faqRes, holidayRes, capsRes] = await Promise.all([
        api.getVoiceProfile(practiceId),
        api.listVoiceFaqs(practiceId),
        api.listVoiceHolidays(practiceId),
        api.getVoiceCapabilities(practiceId).catch(() => ({ data: { packs: [] } }))
      ]);
      applyProfile(profileRes.data?.profile);
      setReadiness(profileRes.data?.readiness || []);
      setCapabilities(capsRes.data?.packs || []);
      setFaqs(faqRes.data || []);
      setHolidays(holidayRes.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load practice profile');
    } finally {
      setLoading(false);
    }
  }, [practiceId, applyProfile]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const togglePack = async (id) => {
    if (!isSuperAdmin || savingCaps) return;
    const next = capabilities.map(p => p.id === id ? { ...p, enabled: !p.enabled } : p);
    setSavingCaps(true);
    setError(null);
    try {
      const res = await api.setVoiceCapabilities(practiceId, next.filter(p => p.enabled).map(p => p.id));
      setCapabilities(res.data?.packs || next);
      if (res.data?.readiness) setReadiness(res.data.readiness);
      showSuccess('Capabilities saved. The next call uses the new set.');
    } catch (e) {
      showError(e.message || 'Could not save capabilities');
    } finally {
      setSavingCaps(false);
    }
  };

  const recheckReadiness = async () => {
    setCheckingReadiness(true);
    try {
      const res = await api.getVoiceReadiness(practiceId);
      setReadiness(res.data || []);
    } catch (err) {
      showError(err.message || 'Failed to re-check readiness');
    } finally {
      setCheckingReadiness(false);
    }
  };

  const setDay = (day, patch) => setHours(prev => ({ ...prev, [day]: { ...prev[day], ...patch } }));
  const setAddressField = (key, value) => setAddress(prev => ({ ...prev, [key]: value }));

  const addInsurance = () => {
    const name = newInsurance.trim();
    if (!name) return;
    if (insurances.some(i => i.name.toLowerCase() === name.toLowerCase())) {
      setNewInsurance('');
      return;
    }
    setInsurances(prev => [...prev, { name, short: '' }]);
    setNewInsurance('');
  };
  const removeInsurance = (index) => setInsurances(prev => prev.filter((_, i) => i !== index));

  const addContact = () => setContacts(prev => [...prev, { method: 'sms', destination: '', label: '' }]);
  const setContact = (index, patch) => setContacts(prev => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  const removeContact = (index) => setContacts(prev => prev.filter((_, i) => i !== index));

  const saveProfile = async () => {
    const hoursJson = {};
    for (const { key } of PROFILE_DAYS) {
      const d = hours[key];
      hoursJson[key] = d.closed ? null : { open: d.open, close: d.close };
    }
    const patch = {
      hours_json: hoursJson,
      address_json: address,
      office_phone: officePhone.trim() || null,
      cancellation_window_hours: cancellationWindow === '' ? null : Number(cancellationWindow),
      cancellation_late_flag_message: lateMessage.trim() || null,
      accepted_insurances: insurances.map(i => (i.short ? { name: i.name, short: i.short } : { name: i.name })),
      emergency_alert_contacts: contacts.map(c => ({ method: c.method, destination: c.destination.trim(), label: c.label.trim() }))
    };
    setSaving(true);
    try {
      const res = await api.updateVoiceProfile(practiceId, patch);
      applyProfile(res.data);
      showSuccess('Practice profile saved');
      await recheckReadiness();
    } catch (err) {
      showError(err.message || 'Failed to save practice profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingState}>
        <LoadingSpinner />
        <span>Loading practice profile...</span>
      </div>
    );
  }

  const readyCount = readiness.filter(r => r.ok).length;

  return (
    <>
      {error && (
        <div style={styles.alertError}>
          <AlertIcon />
          <span>{error}</span>
          <button onClick={() => setError(null)} style={styles.alertClose}><CloseIcon /></button>
        </div>
      )}
      {success && (
        <div style={styles.alertSuccess}>
          <CheckCircleIcon />
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} style={styles.alertClose}><CloseIcon /></button>
        </div>
      )}

      {/* (a) Readiness checklist */}
      <div style={styles.settingsSection}>
        <div style={profileStyles.sectionHeaderRow}>
          <div>
            <h3 style={styles.sectionHeader}>Launch Readiness</h3>
            <p style={styles.sectionDescription}>
              {readiness.length ? `${readyCount} of ${readiness.length} checks passing.` : 'No checks returned.'} Every row must be green before inbound calls go live.
            </p>
          </div>
          <button type="button" style={styles.editButton} onClick={recheckReadiness} disabled={checkingReadiness}>
            {checkingReadiness ? 'Checking...' : 'Re-check'}
          </button>
        </div>
        <div>
          {readiness.map(check => (
            <div key={check.key} style={profileStyles.readinessRow}>
              <span style={{ ...profileStyles.readinessDot, backgroundColor: check.ok ? '#10b981' : '#ef4444' }} />
              <span style={profileStyles.readinessLabel}>{check.label}</span>
              <span style={{ ...profileStyles.readinessDetail, color: check.ok ? '#6b7280' : '#b91c1c' }}>{check.detail}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Capability packs — what the assistant is allowed to DO. super_admin only. */}
      <div style={styles.settingsSection}>
        <h3 style={styles.sectionHeader}>Capabilities</h3>
        <p style={styles.sectionDescription}>
          What the assistant can do on this practice's line. Facts (hours, providers, policies) come from the sections below; behaviour is managed by Newton Health.
          {!isSuperAdmin && ' Contact Newton Health to change these.'}
        </p>
        <div>
          {capabilities.map(pack => (
            <label key={pack.id} style={{ ...profileStyles.readinessRow, cursor: isSuperAdmin ? 'pointer' : 'default' }}>
              <input
                type="checkbox"
                checked={!!pack.enabled}
                disabled={!isSuperAdmin || savingCaps}
                onChange={() => togglePack(pack.id)}
                style={{ marginRight: 10 }}
              />
              <span style={profileStyles.readinessLabel}>{pack.label}</span>
              <span style={profileStyles.readinessDetail}>
                {pack.tools.length ? `Tools: ${pack.tools.join(', ')}` : 'No tools — changes how the call is handled'}
                {pack.requires.length ? ` · needs ${pack.requires.join(', ')}` : ''}
              </span>
            </label>
          ))}
          {!capabilities.length && <p style={styles.noConfigText}>Capabilities could not be loaded.</p>}
        </div>
      </div>

      {/* (b) Hours */}
      <div style={styles.settingsSection}>
        <h3 style={styles.sectionHeader}>Office Hours</h3>
        <p style={styles.sectionDescription}>Read back to callers by the AI. Times are in the practice timezone, 24-hour format.</p>
        {PROFILE_DAYS.map(({ key, label }) => {
          const d = hours[key];
          return (
            <div key={key} style={profileStyles.hoursRow}>
              <span style={profileStyles.hoursDay}>{label}</span>
              <label style={profileStyles.inlineCheck}>
                <input type="checkbox" checked={!d.closed} onChange={e => setDay(key, { closed: !e.target.checked })} />
                <span>{d.closed ? 'Closed' : 'Open'}</span>
              </label>
              <input
                type="time"
                value={d.open}
                disabled={d.closed}
                onChange={e => setDay(key, { open: e.target.value })}
                style={{ ...styles.input, width: '130px' }}
              />
              <span style={profileStyles.hoursDash}>to</span>
              <input
                type="time"
                value={d.close}
                disabled={d.closed}
                onChange={e => setDay(key, { close: e.target.value })}
                style={{ ...styles.input, width: '130px' }}
              />
            </div>
          );
        })}
      </div>

      {/* (c) Address + (d) Office phone */}
      <div style={styles.settingsSection}>
        <h3 style={styles.sectionHeader}>Address &amp; Office Phone</h3>
        <div style={profileStyles.grid2}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Street</label>
            <input type="text" value={address.street} onChange={e => setAddressField('street', e.target.value)} style={styles.input} placeholder="123 Main St" />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Suite</label>
            <input type="text" value={address.suite} onChange={e => setAddressField('suite', e.target.value)} style={styles.input} placeholder="Suite 200" />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>City</label>
            <input type="text" value={address.city} onChange={e => setAddressField('city', e.target.value)} style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>State</label>
            <input type="text" value={address.state} onChange={e => setAddressField('state', e.target.value)} style={styles.input} placeholder="TX" />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>ZIP</label>
            <input type="text" value={address.zip} onChange={e => setAddressField('zip', e.target.value)} style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Country</label>
            <input type="text" value={address.country} onChange={e => setAddressField('country', e.target.value)} style={styles.input} placeholder="US" />
          </div>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Office phone</label>
          <input type="tel" value={officePhone} onChange={e => setOfficePhone(e.target.value)} style={{ ...styles.input, maxWidth: '320px' }} placeholder="(972) 555-0100" />
          <p style={styles.helpText}>The number the AI reads back when a caller asks how to reach the office.</p>
        </div>
      </div>

      {/* (e) Cancellation policy */}
      <div style={styles.settingsSection}>
        <h3 style={styles.sectionHeader}>Cancellation Policy</h3>
        <div style={styles.formGroup}>
          <label style={styles.label}>Cancellation window (hours)</label>
          <input
            type="number"
            min="0"
            max="168"
            value={cancellationWindow}
            onChange={e => setCancellationWindow(e.target.value)}
            style={{ ...styles.input, maxWidth: '160px' }}
          />
          <p style={styles.helpText}>Cancellations inside this window are flagged as late (0–168).</p>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Late-cancel message</label>
          <textarea
            rows={3}
            value={lateMessage}
            onChange={e => setLateMessage(e.target.value)}
            style={styles.textarea}
            placeholder="We understand. Please note cancellations within 24 hours may be subject to a fee."
          />
        </div>
      </div>

      {/* (f) Accepted insurances */}
      <div style={styles.settingsSection}>
        <h3 style={styles.sectionHeader}>Accepted Insurances</h3>
        <p style={styles.sectionDescription}>Practice-level list only. The AI answers "do you take X?" from here and escalates patient-specific coverage questions.</p>
        <div style={profileStyles.tagList}>
          {insurances.length === 0 && <span style={styles.noConfigText}>No insurances added yet</span>}
          {insurances.map((ins, index) => (
            <span key={`${ins.name}-${index}`} style={profileStyles.tag}>
              {ins.name}
              <button type="button" style={profileStyles.tagRemove} onClick={() => removeInsurance(index)} aria-label={`Remove ${ins.name}`}>&times;</button>
            </span>
          ))}
        </div>
        <div style={profileStyles.inlineForm}>
          <input
            type="text"
            value={newInsurance}
            onChange={e => setNewInsurance(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addInsurance(); } }}
            style={{ ...styles.input, maxWidth: '360px' }}
            placeholder="Blue Cross Blue Shield"
          />
          <button type="button" style={styles.editButton} onClick={addInsurance}>Add</button>
        </div>
      </div>

      {/* (g) Emergency contacts */}
      <div style={styles.settingsSection}>
        <h3 style={styles.sectionHeader}>Emergency Alert Contacts</h3>
        <p style={styles.sectionDescription}>Paged immediately when a caller describes an emergency. At least one contact is required for launch.</p>
        {contacts.length === 0 && <p style={styles.noConfigText}>No contacts configured — nobody will be alerted.</p>}
        {contacts.map((c, index) => (
          <div key={index} style={profileStyles.contactRow}>
            <select value={c.method} onChange={e => setContact(index, { method: e.target.value })} style={{ ...styles.selectInput, width: '110px', padding: '10px 12px' }}>
              <option value="sms">SMS</option>
              <option value="email">Email</option>
            </select>
            <input
              type={c.method === 'email' ? 'email' : 'tel'}
              value={c.destination}
              onChange={e => setContact(index, { destination: e.target.value })}
              style={{ ...styles.input, flex: 1 }}
              placeholder={c.method === 'email' ? 'oncall@practice.com' : '+19725550100'}
            />
            <input
              type="text"
              value={c.label}
              onChange={e => setContact(index, { label: e.target.value })}
              style={{ ...styles.input, flex: 1 }}
              placeholder="Label (e.g. Front desk)"
            />
            <button type="button" style={styles.cancelButton} onClick={() => removeContact(index)}>Remove</button>
          </div>
        ))}
        <button type="button" style={styles.editButton} onClick={addContact}>Add contact</button>
      </div>

      <div style={{ ...styles.editActions, marginBottom: '24px' }}>
        <button type="button" style={styles.saveButton} onClick={saveProfile} disabled={saving}>
          {saving ? 'Saving...' : 'Save profile'}
        </button>
      </div>

      {/* (h) FAQs */}
      <FaqSection practiceId={practiceId} faqs={faqs} onChange={setFaqs} onError={showError} onSuccess={showSuccess} />

      {/* (i) Holidays */}
      <HolidaySection practiceId={practiceId} holidays={holidays} onChange={setHolidays} onError={showError} onSuccess={showSuccess} />
    </>
  );
}

const EMPTY_FAQ_DRAFT = { question: '', question_keywords: '', answer_en: '', answer_es: '', active: true };

function FaqSection({ practiceId, faqs, onChange, onError, onSuccess }) {
  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const startEdit = (faq) => {
    setAdding(false);
    setEditingId(faq.id);
    setDraft({
      question: faq.question || '',
      question_keywords: (Array.isArray(faq.question_keywords) ? faq.question_keywords : []).join(', '),
      answer_en: faq.answer_en || '',
      answer_es: faq.answer_es || '',
      active: faq.active !== false
    });
  };
  const startAdd = () => { setEditingId(null); setAdding(true); setDraft({ ...EMPTY_FAQ_DRAFT }); };
  const cancel = () => { setEditingId(null); setAdding(false); setDraft(null); };
  const setField = (key, value) => setDraft(prev => ({ ...prev, [key]: value }));

  const save = async () => {
    if (!draft.question.trim() || !draft.answer_en.trim()) {
      onError('Question and English answer are required');
      return;
    }
    setBusy(true);
    try {
      if (adding) {
        const res = await api.createVoiceFaq(practiceId, draft);
        onChange([...faqs, res.data]);
        onSuccess('FAQ added');
      } else {
        const res = await api.updateVoiceFaq(practiceId, editingId, draft);
        onChange(faqs.map(f => (f.id === editingId ? res.data : f)));
        onSuccess('FAQ updated');
      }
      cancel();
    } catch (err) {
      onError(err.message || 'Failed to save FAQ');
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (faq) => {
    setBusy(true);
    try {
      const res = await api.updateVoiceFaq(practiceId, faq.id, { active: faq.active === false });
      onChange(faqs.map(f => (f.id === faq.id ? res.data : f)));
    } catch (err) {
      onError(err.message || 'Failed to update FAQ');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (faq) => {
    if (!window.confirm(`Delete FAQ "${faq.question}"?`)) return;
    setBusy(true);
    try {
      await api.deleteVoiceFaq(practiceId, faq.id);
      onChange(faqs.filter(f => f.id !== faq.id));
      onSuccess('FAQ deleted');
      if (editingId === faq.id) cancel();
    } catch (err) {
      onError(err.message || 'Failed to delete FAQ');
    } finally {
      setBusy(false);
    }
  };

  const editor = draft && (
    <div style={styles.editForm}>
      <div style={profileStyles.grid2}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Question <span style={styles.required}>*</span></label>
          <input type="text" value={draft.question} onChange={e => setField('question', e.target.value)} style={styles.input} placeholder="Where do I park?" />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Keywords (comma-separated)</label>
          <input type="text" value={draft.question_keywords} onChange={e => setField('question_keywords', e.target.value)} style={styles.input} placeholder="parking, lot, garage" />
          <p style={styles.helpText}>The AI matches caller questions against these words.</p>
        </div>
      </div>
      <div style={styles.formGroup}>
        <label style={styles.label}>Answer (English) <span style={styles.required}>*</span></label>
        <textarea rows={3} value={draft.answer_en} onChange={e => setField('answer_en', e.target.value)} style={styles.textarea} />
      </div>
      <div style={styles.formGroup}>
        <label style={styles.label}>Answer (Spanish)</label>
        <textarea rows={3} value={draft.answer_es} onChange={e => setField('answer_es', e.target.value)} style={styles.textarea} />
      </div>
      <label style={profileStyles.inlineCheck}>
        <input type="checkbox" checked={draft.active} onChange={e => setField('active', e.target.checked)} />
        <span>Active</span>
      </label>
      <div style={styles.editActions}>
        <button type="button" style={styles.cancelButton} onClick={cancel} disabled={busy}>Cancel</button>
        <button type="button" style={styles.saveButton} onClick={save} disabled={busy}>{busy ? 'Saving...' : (adding ? 'Add FAQ' : 'Save FAQ')}</button>
      </div>
    </div>
  );

  return (
    <div style={styles.settingsSection}>
      <div style={profileStyles.sectionHeaderRow}>
        <div>
          <h3 style={styles.sectionHeader}>FAQs</h3>
          <p style={styles.sectionDescription}>Only stored answers are spoken — anything unmatched routes to a callback.</p>
        </div>
        <button type="button" style={styles.editButton} onClick={startAdd} disabled={busy || adding}>Add FAQ</button>
      </div>
      {faqs.length === 0 && !adding && <p style={styles.noConfigText}>No FAQs yet</p>}
      {faqs.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={profileStyles.table}>
            <thead>
              <tr>
                <th style={profileStyles.th}>Question</th>
                <th style={profileStyles.th}>Keywords</th>
                <th style={profileStyles.th}>Answer (EN)</th>
                <th style={profileStyles.th}>Answer (ES)</th>
                <th style={profileStyles.th}>Active</th>
                <th style={profileStyles.th} />
              </tr>
            </thead>
            <tbody>
              {faqs.map(faq => (
                <React.Fragment key={faq.id}>
                  <tr style={{ opacity: faq.active === false ? 0.6 : 1 }}>
                    <td style={profileStyles.td}>{faq.question}</td>
                    <td style={profileStyles.td}>{(Array.isArray(faq.question_keywords) ? faq.question_keywords : []).join(', ')}</td>
                    <td style={profileStyles.td}>{faq.answer_en}</td>
                    <td style={profileStyles.td}>{faq.answer_es || <span style={styles.noConfigText}>—</span>}</td>
                    <td style={profileStyles.td}>
                      <input type="checkbox" checked={faq.active !== false} onChange={() => toggleActive(faq)} disabled={busy} />
                    </td>
                    <td style={{ ...profileStyles.td, whiteSpace: 'nowrap' }}>
                      <button type="button" style={profileStyles.linkButton} onClick={() => startEdit(faq)} disabled={busy}>Edit</button>
                      <button type="button" style={{ ...profileStyles.linkButton, color: '#dc2626' }} onClick={() => remove(faq)} disabled={busy}>Delete</button>
                    </td>
                  </tr>
                  {editingId === faq.id && (
                    <tr>
                      <td colSpan={6} style={{ ...profileStyles.td, backgroundColor: '#f9fafb' }}>{editor}</td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {adding && editor}
    </div>
  );
}

const EMPTY_HOLIDAY_DRAFT = { holiday_date: '', name: '', is_full_day: true, open: '09:00', close: '13:00' };

function HolidaySection({ practiceId, holidays, onChange, onError, onSuccess }) {
  const [draft, setDraft] = useState({ ...EMPTY_HOLIDAY_DRAFT });
  const [busy, setBusy] = useState(false);

  const setField = (key, value) => setDraft(prev => ({ ...prev, [key]: value }));

  const add = async () => {
    if (!draft.holiday_date || !draft.name.trim()) {
      onError('Holiday date and name are required');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        holiday_date: draft.holiday_date,
        name: draft.name.trim(),
        is_full_day: draft.is_full_day,
        partial_hours_json: draft.is_full_day ? null : { open: draft.open, close: draft.close }
      };
      const res = await api.createVoiceHoliday(practiceId, payload);
      // Create upserts on (practice, date): replace an existing row for that date.
      const others = holidays.filter(h => h.id !== res.data?.id && String(h.holiday_date).slice(0, 10) !== draft.holiday_date);
      onChange([...others, res.data].sort((a, b) => String(a.holiday_date).localeCompare(String(b.holiday_date))));
      onSuccess('Holiday added');
      setDraft({ ...EMPTY_HOLIDAY_DRAFT });
    } catch (err) {
      onError(err.message || 'Failed to add holiday');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (holiday) => {
    if (!window.confirm(`Remove holiday "${holiday.name}"?`)) return;
    setBusy(true);
    try {
      await api.deleteVoiceHoliday(practiceId, holiday.id);
      onChange(holidays.filter(h => h.id !== holiday.id));
      onSuccess('Holiday removed');
    } catch (err) {
      onError(err.message || 'Failed to remove holiday');
    } finally {
      setBusy(false);
    }
  };

  const formatHolidayDate = (value) => String(value || '').slice(0, 10);
  const formatPartial = (h) => {
    const p = typeof h.partial_hours_json === 'string' ? (() => { try { return JSON.parse(h.partial_hours_json); } catch { return null; } })() : h.partial_hours_json;
    return p && p.open && p.close ? `${p.open}–${p.close}` : '';
  };

  return (
    <div style={styles.settingsSection}>
      <h3 style={styles.sectionHeader}>Holidays &amp; Closures</h3>
      <p style={styles.sectionDescription}>The AI will not offer appointment slots on full-day closures and mentions them when callers ask about hours.</p>
      {holidays.length === 0 && <p style={styles.noConfigText}>No holidays scheduled</p>}
      {holidays.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={profileStyles.table}>
            <thead>
              <tr>
                <th style={profileStyles.th}>Date</th>
                <th style={profileStyles.th}>Name</th>
                <th style={profileStyles.th}>Closure</th>
                <th style={profileStyles.th} />
              </tr>
            </thead>
            <tbody>
              {holidays.map(h => (
                <tr key={h.id}>
                  <td style={profileStyles.td}>{formatHolidayDate(h.holiday_date)}</td>
                  <td style={profileStyles.td}>{h.name}</td>
                  <td style={profileStyles.td}>{h.is_full_day === false ? `Partial ${formatPartial(h)}`.trim() : 'Full day'}</td>
                  <td style={{ ...profileStyles.td, whiteSpace: 'nowrap' }}>
                    <button type="button" style={{ ...profileStyles.linkButton, color: '#dc2626' }} onClick={() => remove(h)} disabled={busy}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ ...profileStyles.contactRow, marginTop: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ ...styles.formGroup, marginBottom: 0 }}>
          <label style={styles.label}>Date</label>
          <input type="date" value={draft.holiday_date} onChange={e => setField('holiday_date', e.target.value)} style={{ ...styles.input, width: '170px' }} />
        </div>
        <div style={{ ...styles.formGroup, marginBottom: 0, flex: 1, minWidth: '180px' }}>
          <label style={styles.label}>Name</label>
          <input type="text" value={draft.name} onChange={e => setField('name', e.target.value)} style={styles.input} placeholder="Thanksgiving" />
        </div>
        <label style={{ ...profileStyles.inlineCheck, paddingBottom: '10px' }}>
          <input type="checkbox" checked={draft.is_full_day} onChange={e => setField('is_full_day', e.target.checked)} />
          <span>Full day</span>
        </label>
        {!draft.is_full_day && (
          <>
            <input type="time" value={draft.open} onChange={e => setField('open', e.target.value)} style={{ ...styles.input, width: '130px' }} />
            <span style={profileStyles.hoursDash}>to</span>
            <input type="time" value={draft.close} onChange={e => setField('close', e.target.value)} style={{ ...styles.input, width: '130px' }} />
          </>
        )}
        <button type="button" style={styles.saveButton} onClick={add} disabled={busy}>{busy ? 'Saving...' : 'Add holiday'}</button>
      </div>
    </div>
  );
}

const profileStyles = {
  sectionHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '8px'
  },
  readinessRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0',
    borderBottom: '1px solid #f3f4f6',
    fontSize: '14px'
  },
  readinessDot: {
    flexShrink: 0,
    width: '10px',
    height: '10px',
    borderRadius: '50%'
  },
  readinessLabel: {
    flex: '0 0 260px',
    fontWeight: 500,
    color: '#374151'
  },
  readinessDetail: {
    flex: 1,
    fontSize: '13px'
  },
  hoursRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px'
  },
  hoursDay: {
    width: '100px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151'
  },
  hoursDash: {
    fontSize: '13px',
    color: '#6b7280'
  },
  inlineCheck: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#374151',
    width: '90px',
    cursor: 'pointer'
  },
  grid2: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    columnGap: '16px'
  },
  tagList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '12px'
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    backgroundColor: '#eff6ff',
    color: '#1d4ed8',
    borderRadius: '999px',
    fontSize: '13px'
  },
  tagRemove: {
    background: 'none',
    border: 'none',
    color: 'inherit',
    cursor: 'pointer',
    fontSize: '16px',
    lineHeight: 1,
    padding: 0
  },
  inlineForm: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  contactRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px'
  },
  th: {
    textAlign: 'left',
    padding: '8px',
    borderBottom: '2px solid #e5e7eb',
    color: '#6b7280',
    fontWeight: 600,
    fontSize: '12px',
    textTransform: 'uppercase'
  },
  td: {
    padding: '8px',
    borderBottom: '1px solid #f3f4f6',
    verticalAlign: 'top',
    color: '#374151'
  },
  linkButton: {
    background: 'none',
    border: 'none',
    color: '#3b82f6',
    cursor: 'pointer',
    fontSize: '13px',
    padding: '0 6px'
  }
};

function formatOutcome(outcome) {
  const labels = {
    booked: 'Booked',
    interested: 'Interested',
    declined: 'Declined',
    voicemail: 'Voicemail',
    no_answer: 'No Answer',
    failed: 'Failed',
    opt_out: 'Opted Out',
    callback_requested: 'Callback',
    appointment_booked: 'Appointment Booked',
    appointment_canceled: 'Appointment Canceled',
    appointment_rescheduled: 'Appointment Rescheduled',
    info_provided: 'Info Provided',
    wrong_number: 'Wrong Number',
    abandoned: 'Caller Hung Up',
    no_resolution: 'No Resolution',
    error: 'Error'
  };
  return labels[outcome] || outcome || 'N/A';
}

function formatEndReason(reason) {
  const labels = {
    caller_hangup: 'Caller hung up',
    ai_completed: 'AI completed',
    ai_escalated: 'Escalated to callback',
    ai_error: 'AI error',
    silence_timeout: 'Caller went silent',
    transferred: 'Transferred',
    voicemail_left: 'Voicemail left',
    agent_ended: 'Agent ended call'
  };
  return labels[reason] || reason || 'N/A';
}

/**
 * Strip internal protocol markers (e.g. {"interrupted":true}) and
 * empty/duplicate role lines from the persisted transcript before
 * showing it to humans. Backend persists the same scrub on save going
 * forward, but old rows still need this client-side cleanup.
 */
function cleanTranscriptForDisplay(transcript) {
  if (!transcript) return '';
  const lines = transcript.split('\n');
  const out = [];
  let lastLine = '';
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    // Drop protocol/internal JSON markers
    if (/^(AI|Patient):\s*\{.*"interrupted".*\}\s*$/.test(line)) continue;
    if (/^(AI|Patient):\s*\{.*\}\s*$/.test(line) && line.length < 80) continue;
    // Drop empty role lines
    if (/^(AI|Patient):\s*$/.test(line)) continue;
    // Collapse exact-duplicate consecutive lines (barge-in artifact)
    if (line === lastLine) continue;
    out.push(line);
    lastLine = line;
  }
  return out.join('\n');
}

/**
 * Vapi-style structured analysis panel: outcome reason, structured data,
 * success rubric, and a re-analyze button for refreshing after fixes.
 */
function CallAnalysisPanel({ call }) {
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);

  const structured = call.structured_data
    ? (typeof call.structured_data === 'string' ? JSON.parse(call.structured_data) : call.structured_data)
    : null;
  const success = call.success_evaluation
    ? (typeof call.success_evaluation === 'string' ? JSON.parse(call.success_evaluation) : call.success_evaluation)
    : null;

  // Tool calls captured during the session — input + result so we can verify
  // what the AI actually saw (e.g. "did the appointment lookup return the
  // right date?"). Older calls may have no result field; tolerate both.
  let toolCalls = [];
  try {
    const meta = call.call_metadata
      ? (typeof call.call_metadata === 'string' ? JSON.parse(call.call_metadata) : call.call_metadata)
      : null;
    if (meta && Array.isArray(meta.tool_calls)) toolCalls = meta.tool_calls;
  } catch {}

  const handleAnalyze = async () => {
    if (analyzing) return;
    setAnalyzing(true);
    setError(null);
    try {
      // Use api helper so auth token + base URL are handled the same as the
      // rest of the app (raw fetch with localStorage 'token' was wrong — the
      // actual key is 'nh_auth_token' and api.request abstracts that).
      const r = await api.analyzeCall(call.practice_id, call.id);
      if (!r.success) throw new Error(r.error || 'Analyze failed');
      window.location.reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const status = call.analysis_status;
  const hasAnalysis = !!(structured || success || call.ai_summary);

  return (
    <div style={{ marginTop: 16, padding: 16, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827' }}>Call Analysis</h3>
        <button
          onClick={handleAnalyze}
          disabled={analyzing}
          style={{
            padding: '4px 12px', fontSize: 12, borderRadius: 6,
            background: analyzing ? '#e5e7eb' : '#3b82f6', color: analyzing ? '#6b7280' : '#fff',
            border: 'none', cursor: analyzing ? 'wait' : 'pointer', fontWeight: 500
          }}
        >
          {analyzing ? 'Analyzing...' : hasAnalysis ? 'Re-analyze' : 'Analyze'}
        </button>
      </div>

      {/* Run IDs — surface so anyone debugging can hop straight to logs */}
      <CallRunIds call={call} />

      {error && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 8 }}>Error: {error}</div>}
      {status === 'failed' && call.analysis_error && (
        <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 8 }}>Analysis failed: {call.analysis_error}</div>
      )}
      {status === 'pending' && !hasAnalysis && (
        <div style={{ color: '#6b7280', fontSize: 13 }}>Not analyzed yet. Click "Analyze" to run.</div>
      )}
      {status === 'running' && (
        <div style={{ color: '#6b7280', fontSize: 13 }}>Analysis in progress...</div>
      )}

      {success && (
        <div style={{ marginBottom: 12, padding: 10, background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Success</span>
            <span style={{
              padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
              background: success.passed ? '#dcfce7' : '#fee2e2',
              color: success.passed ? '#166534' : '#991b1b'
            }}>{success.passed ? 'PASSED' : 'FAILED'}</span>
            <span style={{ fontSize: 12, color: '#6b7280' }}>Score: {success.score ?? '—'}/100</span>
          </div>
          {success.rubric && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{success.rubric}</div>}
          {Array.isArray(success.issues) && success.issues.length > 0 && (
            <ul style={{ margin: '6px 0 0 18px', padding: 0, fontSize: 12, color: '#7c2d12' }}>
              {success.issues.map((iss, i) => <li key={i}>{iss}</li>)}
            </ul>
          )}
        </div>
      )}

      {structured && (
        <div style={{ padding: 10, background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Extracted Data</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: 12 }}>
            {structured.patient_intent && (<><span style={{ color: '#6b7280' }}>Intent</span><span>{structured.patient_intent}</span></>)}
            <span style={{ color: '#6b7280' }}>Patient identified</span><span>{structured.patient_identified ? 'Yes' : 'No'}</span>
            {structured.appointment_details && (
              <>
                <span style={{ color: '#6b7280' }}>Appointment</span>
                <span>
                  {[structured.appointment_details.date, structured.appointment_details.time, structured.appointment_details.provider].filter(Boolean).join(' · ') || '—'}
                </span>
              </>
            )}
            {structured.callback_created && (
              <>
                <span style={{ color: '#6b7280' }}>Callback</span>
                <span>Yes — {structured.callback_reason || ''}</span>
              </>
            )}
            {Array.isArray(structured.tools_used) && structured.tools_used.length > 0 && (
              <>
                <span style={{ color: '#6b7280' }}>Tools used</span>
                <span>{structured.tools_used.join(', ')}</span>
              </>
            )}
            {Array.isArray(structured.key_issues) && structured.key_issues.length > 0 && (
              <>
                <span style={{ color: '#6b7280' }}>Issues</span>
                <span style={{ color: '#7c2d12' }}>{structured.key_issues.join('; ')}</span>
              </>
            )}
          </div>
        </div>
      )}

      {toolCalls.length > 0 && <ToolCallsList toolCalls={toolCalls} />}
    </div>
  );
}

/**
 * Surface the run IDs so anyone debugging can hop directly from this panel
 * to the right CloudWatch logs / DB row, without grep-fishing across the
 * task log (which is task-wide and overlaps with other concurrent calls —
 * the wrong layer to filter at).
 *
 * Twilio CallSid (voice_session_id) is the cleanest ID to filter logs by:
 * inbound webhook + Stream start + every Nova Tools log line that mentions
 * the call all carry it.
 */
function CallRunIds({ call }) {
  const callSid = call.voice_session_id || null;
  const [copied, setCopied] = useState(null);

  const copy = (label, text) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 1200);
  };

  const logFilter = callSid
    ? `aws logs tail /ecs/newtonhealth-connect-prod --since 1h --filter-pattern '${callSid}' --region us-east-1`
    : null;
  const analyzeCmd = callSid
    ? `node scripts/analyze-call.js ${callSid}`
    : null;

  if (!callSid && !call.id) return null;

  return (
    <div style={{ marginBottom: 12, padding: 8, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: 11, fontFamily: 'monospace' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 6, alignItems: 'center', columnGap: 8 }}>
        <span style={{ color: '#6b7280' }}>call.id</span>
        <code style={{ wordBreak: 'break-all' }}>{call.id}</code>
        <button onClick={() => copy('id', String(call.id))} style={miniBtn}>{copied === 'id' ? '✓' : 'copy'}</button>

        {callSid && <>
          <span style={{ color: '#6b7280' }}>CallSid</span>
          <code style={{ wordBreak: 'break-all' }}>{callSid}</code>
          <button onClick={() => copy('sid', callSid)} style={miniBtn}>{copied === 'sid' ? '✓' : 'copy'}</button>
        </>}

        {call.voice_flow_id && <>
          <span style={{ color: '#6b7280' }}>flow / version</span>
          <code>flow={call.voice_flow_id}, version={call.voice_flow_version_id}</code>
          <span />
        </>}
      </div>
      {callSid && (
        <details style={{ marginTop: 6 }}>
          <summary style={{ cursor: 'pointer', color: '#3b82f6', fontSize: 10 }}>Debug snippets</summary>
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <code style={{ flex: 1, padding: 4, background: '#f9fafb', borderRadius: 3, fontSize: 10, wordBreak: 'break-all' }}>{logFilter}</code>
              <button onClick={() => copy('logfilter', logFilter)} style={miniBtn}>{copied === 'logfilter' ? '✓' : 'copy'}</button>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <code style={{ flex: 1, padding: 4, background: '#f9fafb', borderRadius: 3, fontSize: 10, wordBreak: 'break-all' }}>{analyzeCmd}</code>
              <button onClick={() => copy('analyze', analyzeCmd)} style={miniBtn}>{copied === 'analyze' ? '✓' : 'copy'}</button>
            </div>
            <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
              Run via ECS exec: <code>aws ecs execute-command --cluster newtonhealth-prod --container nhconnect --interactive --command "&lt;cmd above&gt;"</code>
            </div>
          </div>
        </details>
      )}
    </div>
  );
}
const miniBtn = { padding: '2px 6px', fontSize: 10, border: '1px solid #d1d5db', borderRadius: 3, background: '#fff', cursor: 'pointer' };

function ToolCallsList({ toolCalls }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ marginTop: 10, padding: 10, background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb' }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151' }}
      >
        <span>Tool calls ({toolCalls.length})</span>
        <span style={{ fontSize: 11, color: '#6b7280' }}>{expanded ? '▼ collapse' : '▶ expand'}</span>
      </div>
      {expanded && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {toolCalls.map((t, i) => (
            <ToolCallRow key={i} call={t} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}

function ToolCallRow({ call, index }) {
  const [showResult, setShowResult] = useState(false);
  const inputStr = (() => {
    try { return JSON.stringify(call.input ?? {}, null, 2); } catch { return String(call.input); }
  })();
  // result is captured as a JSON string (already truncated server-side); fall
  // back to displaying the raw value for older rows that stored an object.
  const resultStr = (() => {
    if (call.error) return `ERROR: ${call.error}`;
    if (call.result == null) return '(no result captured)';
    if (typeof call.result === 'string') return call.result;
    try { return JSON.stringify(call.result, null, 2); } catch { return String(call.result); }
  })();
  const isError = !!call.error || /"success"\s*:\s*false/.test(resultStr);
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 4, padding: 8, fontSize: 11, fontFamily: 'monospace', background: '#fafafa' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div style={{ fontWeight: 600, color: isError ? '#991b1b' : '#1f2937' }}>
          #{index + 1} {call.name}
          {call.duration_ms != null && <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 6 }}>({call.duration_ms}ms)</span>}
        </div>
        <button
          onClick={() => setShowResult(s => !s)}
          style={{ padding: '2px 6px', fontSize: 10, border: '1px solid #d1d5db', borderRadius: 3, background: '#fff', cursor: 'pointer' }}
        >
          {showResult ? 'hide result' : 'show result'}
        </button>
      </div>
      <div style={{ color: '#6b7280', marginBottom: 4 }}>input:</div>
      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#1f2937' }}>{inputStr}</pre>
      {showResult && (
        <>
          <div style={{ color: '#6b7280', margin: '6px 0 4px' }}>result:</div>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: isError ? '#991b1b' : '#065f46', maxHeight: 240, overflow: 'auto' }}>{resultStr}</pre>
        </>
      )}
    </div>
  );
}

// Icon Components
function PhoneIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function PhonePlusIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
      <line x1="19" y1="1" x2="19" y2="7" />
      <line x1="16" y1="4" x2="22" y2="4" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function PhoneOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function CallbackIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M15.05 5A5 5 0 0 1 19 8.95M15.05 1A9 9 0 0 1 23 8.94m-1 7.98v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

function SparklesIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z" />
    </svg>
  );
}

function TranscriptIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function LoadingSpinner() {
  return (
    <div style={{
      width: '20px',
      height: '20px',
      border: '2px solid #e5e7eb',
      borderTopColor: '#3b82f6',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
  );
}

// Styles
const styles = {
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 120px)',
    backgroundColor: '#f3f4f6',
    margin: '-20px -24px',
    width: 'calc(100% + 48px)'
  },
  noPractice: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 'calc(100vh - 120px)',
    backgroundColor: '#f3f4f6',
    margin: '-20px -24px',
    width: 'calc(100% + 48px)'
  },
  noPracticeCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    color: '#6b7280'
  },
  noPracticeTitle: {
    margin: '16px 0 8px 0',
    color: '#374151',
    fontSize: '18px'
  },
  noPracticeText: {
    margin: 0,
    color: '#6b7280',
    fontSize: '14px'
  },
  statsBar: {
    display: 'flex',
    gap: '16px',
    padding: '16px 24px',
    backgroundColor: '#fff',
    borderBottom: '1px solid #e5e7eb',
    overflowX: 'auto'
  },
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    minWidth: '140px'
  },
  statIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  statInfo: {
    display: 'flex',
    flexDirection: 'column'
  },
  statValue: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#111827'
  },
  statLabel: {
    fontSize: '12px',
    color: '#6b7280'
  },
  tabBar: {
    display: 'flex',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: '#fff',
    borderBottom: '1px solid #e5e7eb'
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#6b7280',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  },
  tabActive: {
    backgroundColor: '#eff6ff',
    color: '#3b82f6'
  },
  badge: {
    padding: '2px 8px',
    backgroundColor: '#ef4444',
    color: '#fff',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600'
  },
  mainContainer: {
    flex: 1,
    overflow: 'hidden'
  },
  splitView: {
    display: 'flex',
    height: '100%'
  },
  sidebar: {
    width: '380px',
    minWidth: '380px',
    borderRight: '1px solid #e5e7eb',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff'
  },
  sidebarHeader: {
    padding: '16px 20px',
    borderBottom: '1px solid #f3f4f6'
  },
  sidebarTitle: {
    margin: '0 0 12px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827'
  },
  filterRow: {
    display: 'flex',
    gap: '8px'
  },
  filterSelect: {
    flex: 1,
    padding: '8px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#374151',
    backgroundColor: '#fff',
    cursor: 'pointer',
    outline: 'none'
  },
  listContainer: {
    flex: 1,
    overflowY: 'auto'
  },
  loadingState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '40px',
    color: '#6b7280',
    fontSize: '14px'
  },
  emptyList: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    color: '#9ca3af',
    textAlign: 'center'
  },
  callItem: {
    padding: '16px 20px',
    borderBottom: '1px solid #f3f4f6',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease'
  },
  callItemSelected: {
    backgroundColor: '#eff6ff'
  },
  callItemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px'
  },
  patientName: {
    fontWeight: '600',
    color: '#111827',
    fontSize: '14px'
  },
  callItemDetails: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
    color: '#6b7280'
  },
  phoneNumber: {
    color: '#6b7280'
  },
  callTime: {
    color: '#9ca3af'
  },
  callTimeRange: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '6px',
    fontSize: '12px',
    color: '#6b7280',
    fontVariantNumeric: 'tabular-nums',
  },
  callDuration: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '6px',
    fontSize: '12px',
    color: '#9ca3af'
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f9fafb',
    overflow: 'hidden'
  },
  errorBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: '#fef2f2',
    borderBottom: '1px solid #fecaca',
    color: '#dc2626',
    fontSize: '14px'
  },
  errorClose: {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    color: '#dc2626',
    display: 'flex'
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    textAlign: 'center',
    color: '#9ca3af'
  },
  emptyTitle: {
    margin: '20px 0 8px 0',
    fontSize: '18px',
    fontWeight: '600',
    color: '#374151'
  },
  emptyText: {
    margin: 0,
    fontSize: '14px',
    color: '#9ca3af'
  },
  callDetail: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px'
  },
  detailHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  detailHeaderInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  patientAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: '600'
  },
  detailPatientName: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827'
  },
  detailPhone: {
    margin: '4px 0 0 0',
    fontSize: '14px',
    color: '#6b7280'
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '24px'
  },
  infoCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  infoCardIcon: {
    color: '#6b7280'
  },
  infoCardContent: {
    display: 'flex',
    flexDirection: 'column'
  },
  infoCardLabel: {
    fontSize: '12px',
    color: '#6b7280',
    marginBottom: '2px'
  },
  infoCardValue: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#111827'
  },
  summarySection: {
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    marginBottom: '16px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    margin: '0 0 12px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827'
  },
  summaryText: {
    margin: 0,
    fontSize: '14px',
    lineHeight: 1.6,
    color: '#374151'
  },
  transcriptSection: {
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    marginBottom: '16px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  transcriptHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px'
  },
  toggleButton: {
    background: 'none',
    border: 'none',
    color: '#3b82f6',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  transcriptContent: {
    overflow: 'hidden',
    transition: 'max-height 0.3s ease'
  },
  transcriptText: {
    margin: 0,
    fontSize: '13px',
    lineHeight: 1.8,
    color: '#374151',
    whiteSpace: 'pre-wrap',
    fontFamily: 'inherit'
  },
  recordingSection: {
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  audioPlayer: {
    width: '100%',
    marginTop: '8px'
  },
  statusBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
    textTransform: 'capitalize'
  },
  statusBadgeLarge: {
    padding: '6px 14px',
    fontSize: '13px'
  },
  badgeSuccess: {
    backgroundColor: '#d1fae5',
    color: '#065f46'
  },
  badgePurple: {
    backgroundColor: '#ede9fe',
    color: '#5b21b6'
  },
  badgeWarning: {
    backgroundColor: '#fef3c7',
    color: '#92400e'
  },
  badgeDanger: {
    backgroundColor: '#fee2e2',
    color: '#991b1b'
  },
  badgeInfo: {
    backgroundColor: '#dbeafe',
    color: '#1e40af'
  },
  badgeGray: {
    backgroundColor: '#f3f4f6',
    color: '#4b5563'
  },
  urgencyBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase'
  },
  urgencyHigh: {
    backgroundColor: '#fee2e2',
    color: '#991b1b'
  },
  urgencyMedium: {
    backgroundColor: '#fef3c7',
    color: '#92400e'
  },
  urgencyLow: {
    backgroundColor: '#f3f4f6',
    color: '#4b5563'
  },
  callbacksContainer: {
    padding: '24px',
    height: '100%',
    overflowY: 'auto',
    backgroundColor: '#f9fafb'
  },
  callbacksHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px'
  },
  callbacksTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    color: '#111827'
  },
  callbacksSubtitle: {
    margin: '4px 0 0 0',
    fontSize: '14px',
    color: '#6b7280'
  },
  refreshButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    cursor: 'pointer'
  },
  emptyCallbacks: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 40px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    color: '#10b981',
    textAlign: 'center'
  },
  callbacksGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: '16px'
  },
  callbackCard: {
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  callbackCardUrgent: {
    borderColor: '#fca5a5',
    borderWidth: '2px'
  },
  callbackCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px'
  },
  callbackPatient: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  callbackPhone: {
    display: 'block',
    fontSize: '13px',
    color: '#6b7280',
    marginTop: '2px'
  },
  callbackReason: {
    padding: '10px 12px',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#374151',
    marginBottom: '12px'
  },
  callbackMeta: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px'
  },
  callbackActions: {
    display: 'flex',
    gap: '8px'
  },
  callNowButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
    textDecoration: 'none',
    cursor: 'pointer'
  },
  completeButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    backgroundColor: '#fff',
    color: '#374151',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    width: '440px',
    maxWidth: '90%',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  modalTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827'
  },
  modalClose: {
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    color: '#9ca3af',
    display: 'flex'
  },
  modalBody: {
    marginBottom: '20px'
  },
  modalText: {
    margin: '0 0 16px 0',
    fontSize: '14px',
    color: '#374151'
  },
  formGroup: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '8px'
  },
  textarea: {
    width: '100%',
    padding: '12px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    resize: 'vertical',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    paddingTop: '16px',
    borderTop: '1px solid #f3f4f6'
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#fff',
    color: '#374151',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  completeButtonPrimary: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: '#10b981',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  // Settings styles
  settingsContainer: {
    padding: '24px',
    height: '100%',
    overflowY: 'auto',
    backgroundColor: '#f9fafb'
  },
  settingsHeader: {
    marginBottom: '24px'
  },
  settingsTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    color: '#111827'
  },
  settingsSubtitle: {
    margin: '4px 0 0 0',
    fontSize: '14px',
    color: '#6b7280'
  },
  settingsContent: {
    maxWidth: '800px'
  },
  settingsSection: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    margin: '0 0 8px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827'
  },
  sectionDescription: {
    margin: '0 0 20px 0',
    fontSize: '14px',
    color: '#6b7280',
    lineHeight: 1.5
  },
  link: {
    color: '#3b82f6',
    textDecoration: 'none'
  },
  assistantsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  assistantCard: {
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '16px',
    backgroundColor: '#fafafa'
  },
  assistantCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px'
  },
  assistantName: {
    margin: 0,
    fontSize: '15px',
    fontWeight: '600',
    color: '#111827'
  },
  assistantSlug: {
    fontSize: '12px',
    color: '#9ca3af',
    fontFamily: 'monospace'
  },
  configuredBadge: {
    padding: '4px 10px',
    backgroundColor: '#d1fae5',
    color: '#065f46',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500'
  },
  notConfiguredBadge: {
    padding: '4px 10px',
    backgroundColor: '#f3f4f6',
    color: '#6b7280',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500'
  },
  assistantDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  detailRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px'
  },
  detailLabel: {
    color: '#6b7280',
    minWidth: '90px'
  },
  codeText: {
    backgroundColor: '#f3f4f6',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: 'monospace',
    color: '#374151'
  },
  noConfigText: {
    margin: 0,
    fontSize: '13px',
    color: '#9ca3af',
    fontStyle: 'italic'
  },
  editButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
    cursor: 'pointer',
    marginTop: '8px',
    alignSelf: 'flex-start'
  },
  editForm: {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid #e5e7eb'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box'
  },
  helpText: {
    margin: '4px 0 0 0',
    fontSize: '12px',
    color: '#9ca3af'
  },
  required: {
    color: '#ef4444'
  },
  editActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '16px'
  },
  saveButton: {
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  alertError: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    color: '#dc2626',
    fontSize: '14px',
    marginBottom: '16px'
  },
  alertSuccess: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: '#d1fae5',
    border: '1px solid #6ee7b7',
    borderRadius: '8px',
    color: '#065f46',
    fontSize: '14px',
    marginBottom: '16px'
  },
  alertClose: {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    color: 'inherit',
    display: 'flex'
  },
  helpSection: {
    backgroundColor: '#eff6ff',
    borderRadius: '12px',
    padding: '20px',
    border: '1px solid #bfdbfe'
  },
  helpTitle: {
    margin: '0 0 12px 0',
    fontSize: '15px',
    fontWeight: '600',
    color: '#1e40af'
  },
  helpList: {
    margin: 0,
    paddingLeft: '20px',
    fontSize: '14px',
    color: '#1e40af',
    lineHeight: 1.8
  },
  // New Call Button styles
  newCallButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    marginRight: '8px'
  },
  // New Call Modal styles
  newCallModal: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    width: '500px',
    maxWidth: '95%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
  },
  searchSection: {
    marginBottom: '16px'
  },
  searchRow: {
    display: 'flex',
    gap: '8px'
  },
  searchInput: {
    flex: 1,
    padding: '12px 14px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.15s ease'
  },
  searchButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    height: '44px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer'
  },
  searchResults: {
    marginTop: '8px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    maxHeight: '200px',
    overflowY: 'auto'
  },
  patientResult: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    padding: '12px',
    backgroundColor: '#fff',
    border: 'none',
    borderBottom: '1px solid #f3f4f6',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background-color 0.15s ease'
  },
  patientResultAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '600'
  },
  patientResultInfo: {
    display: 'flex',
    flexDirection: 'column'
  },
  patientResultName: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#111827'
  },
  patientResultDetails: {
    fontSize: '12px',
    color: '#6b7280'
  },
  selectedPatientCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: '#f9fafb',
    borderRadius: '10px',
    marginBottom: '16px',
    border: '1px solid #e5e7eb'
  },
  selectedPatientInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  patientAvatarLarge: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: '600'
  },
  selectedPatientName: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827'
  },
  selectedPatientPhone: {
    margin: '2px 0 0 0',
    fontSize: '14px',
    color: '#6b7280'
  },
  changePatientButton: {
    padding: '8px 14px',
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
    cursor: 'pointer'
  },
  consentStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    marginBottom: '16px'
  },
  consentOptIn: {
    backgroundColor: '#d1fae5',
    color: '#065f46'
  },
  consentOptOut: {
    backgroundColor: '#fee2e2',
    color: '#991b1b'
  },
  consentUnknown: {
    backgroundColor: '#f3f4f6',
    color: '#4b5563'
  },
  warningBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 14px',
    backgroundColor: '#fef3c7',
    color: '#92400e',
    borderRadius: '8px',
    fontSize: '13px',
    marginBottom: '16px'
  },
  selectInput: {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    outline: 'none',
    boxSizing: 'border-box'
  },
  initiateCallButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: '#10b981',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  initiateCallButtonDisabled: {
    backgroundColor: '#9ca3af',
    cursor: 'not-allowed'
  },
  modalError: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 14px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    color: '#dc2626',
    fontSize: '13px',
    marginBottom: '16px'
  },
  modalErrorClose: {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    color: '#dc2626',
    display: 'flex'
  }
};

// Add CSS animation for spinner
const briefingStyles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '32px',
    minHeight: '100%',
    background: '#f3f4f6'
  },
  card: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
    width: '640px',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '80vh'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb'
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: 0
  },
  subtitle: {
    fontSize: '13px',
    color: '#6b7280',
    margin: '4px 0 0 0'
  },
  cancelBtn: {
    padding: '6px 14px',
    fontSize: '13px',
    color: '#6b7280',
    background: 'transparent',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  summaryArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '0'
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid #e5e7eb'
  },
  backBtn: {
    padding: '8px 20px',
    fontSize: '13px',
    fontWeight: '500',
    color: '#6b7280',
    background: '#fff',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  startCallBtn: {
    padding: '8px 24px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#fff',
    background: '#10b981',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  aiStrip: {
    display: 'flex',
    gap: '12px',
    padding: '12px 24px',
    borderTop: '1px solid #e5e7eb',
    flexWrap: 'wrap',
  },
  aiCard: {
    flex: '1 1 200px',
    padding: '12px 14px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    background: '#f9fafb',
  },
  aiCardLabel: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
};

if (typeof document !== 'undefined' && !document.getElementById('voiceai-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'voiceai-styles';
  styleSheet.textContent = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(styleSheet);
}

export default VoiceAi;
